/** 手机使用要求：记录按字段阅读，金额/薪资对比保留横向表；弹窗表单可换行、主要操作便于触控。只调整显示，不改变数据、权限及审批规则。 */
import { ResponsiveTable } from "../../components/mobile/ResponsiveTable";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useApp } from "../../context/AppContext";
import { useSearchParams } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "../../components/ui/sonner";
import { Loader2, Plus, Sparkles, UserPlus, Briefcase, Trash2, Pencil, ChevronRight, ChevronLeft, CalendarClock, BarChart3, Archive, RotateCcw, Upload, FileSignature, Copy, FileStack, Link2, FileText, Share2, Search, CalendarPlus, Tag, X, QrCode, ChevronsDownUp, ChevronsUpDown, CheckCircle2 } from "lucide-react";
import { MANAGE_ROLES, DEPT_MGR_ROLES, inp, hrScope, ScopeBadge, CUR_SYM, useHrmsManage } from "./hrmsShared";
import { fmtD, fmtDT } from "../../lib/date";
import { CandidateMaterials, FeedbackDetail } from "./CandidateMaterials";
import { OnboardingSendBlock } from "../../components/hrms/OnboardingSendBlock";

const CAN_VIEW = [...MANAGE_ROLES, ...DEPT_MGR_ROLES];
// HRMS 一-9：招聘页的准入 = 角色名单 ∪ 管理员单独授予的招聘菜单。
// 原先只看 CAN_VIEW 硬编码角色，导致「管理员给招聘专员勾了 /hrms/recruiting」后
// 侧栏能进、页面却被这句提示挡住（后端已放行，前端还拦着）。
// 这里改用与后端同一套写法的 featVis：账号级授予优先，其余沿用原角色名单，
// 未授权的账号看到的仍与改造前完全一致。
const RECRUIT_VIEW_KEYS = ["/hrms/recruiting", "/hrms/recruiting::board", "/hrms/recruiting::interviews",
  "/hrms/recruiting::analytics", "/hrms/recruiting::pool"];
const STAGES = {
  new: { label: "新简历", en: "New", cls: "border-slate-300" },
  screening: { label: "初筛", en: "Screening", cls: "border-sky-300" },
  interview: { label: "面试", en: "Interview", cls: "border-indigo-300" },
  offer: { label: "Offer", en: "Offer", cls: "border-violet-300" },
  hired: { label: "已录用", en: "Hired", cls: "border-emerald-300" },
  rejected: { label: "不合适", en: "Rejected", cls: "border-slate-200" },
};
const SOURCES = { indeed: "Indeed", linkedin: "LinkedIn", zip: "ZipRecruiter", craigslist: "Craigslist", referral: "内推", website: "官网", portal: "自助投递", other: "其他" };
const SOURCES_EN = { indeed: "Indeed", linkedin: "LinkedIn", zip: "ZipRecruiter", craigslist: "Craigslist", referral: "Referral", website: "Website", portal: "Portal", other: "Other" };
const INT_MODES = { onsite: "现场", video: "视频", phone: "电话" };
const INT_MODES_EN = { onsite: "Onsite", video: "Video", phone: "Phone" };
const DECISIONS = { pass: "通过", fail: "不通过", hold: "待定" };
const DECISIONS_EN = { pass: "Pass", fail: "Fail", hold: "Hold" };
const DEC_CLS = { pass: "bg-emerald-100 text-emerald-700", fail: "bg-red-100 text-red-600", hold: "bg-amber-100 text-amber-700" };
const VIEWS = [
  { k: "board", label: "招聘看板", en: "Pipeline", icon: Briefcase },
  { k: "interviews", label: "面试安排", en: "Interviews", icon: CalendarClock },
  { k: "analytics", label: "漏斗分析", en: "Analytics", icon: BarChart3 },
  { k: "pool", label: "人才库", en: "Talent Pool", icon: Archive },
];
const scoreBadge = (s, zh = true) => {
  if (s == null) return <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-400">{zh ? "未评分" : "Unscored"}</span>;
  const cls = s >= 80 ? "bg-emerald-100 text-emerald-700" : s >= 60 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-600";
  return <span className={`text-[10px] px-1.5 py-0.5 rounded font-bold ${cls}`}>AI {s}</span>;
};
const EMPTY_JOB = { title: "", role: "member", branch: "hq", openings: 1, description: "", channels: [] };
const EMPTY_CAND = { jobId: "", name: "", phone: "", email: "", source: "indeed", expectedSalary: "", availableDate: "", resumeText: "" };
// 每页条数选项（HRMS 走查一-1：需求明确要求 20/50/100 三档）。
// 与后端 GET /hrms/candidates 的 pageSize 白名单严格一致 —— 改这里必须同步改 backend/hrms_module.py 的校验。
const PAGE_SIZES = [20, 50, 100];
// 看板默认折叠「不合适」列。
// 为什么加这一条（一-1 的配套）：生产实测 849 位候选人里 816 位是 rejected，
// 分页只解决「一次渲染多少」，但 rejected 和 7 份新简历仍会挤在同一屏，
// 而 HR 日常真正要看的是正在推进的那十几个人。折叠后一屏就能看完在办的管道，
// 需要时点开右侧窄条，既有入口又不占主视线。
// 取舍：不直接删掉该列 —— 人才库、二次激活都依赖它，删列会让「拖回 reject」失去落点。

export default function HRMSRecruiting() {
  const { api, user, lang, featVis } = useApp();
  const zh = lang === "zh";
  const T = (z, e) => (zh ? z : e);
  const SRC = zh ? SOURCES : SOURCES_EN;
  const IM = zh ? INT_MODES : INT_MODES_EN;
  const DEC = zh ? DECISIONS : DECISIONS_EN;
  const stLabel = (k) => (zh ? STAGES[k]?.label : STAGES[k]?.en) || k;
  // HRMS 一-9：招聘写操作（发布职位 / 邀面试 / offer / 删候选人…）同理放到 featVis。
  // 后端这一段已全部改为 can_recruit，前端若仍只看角色名单，招聘专员会「能进页面但看不到任何按钮」。
  const recruitGranted = RECRUIT_VIEW_KEYS.some((k) => featVis(k, false));
  const canManage = useHrmsManage(user) || recruitGranted;
  const canView = CAN_VIEW.includes(user.role) || canManage;
  const scope = hrScope(user);
  const jobCur = (jid) => (jobs || []).find((j) => j.id === jid)?.currency || "USD";
  const sym = (jid) => CUR_SYM[jobCur(jid)] || "$";
  const [jobs, setJobs] = useState(null);
  const [cands, setCands] = useState([]);
  /* 分页状态（HRMS 走查一-1）。
     需求原文："Include multiple pages with option of showing 20/50/100 per page."
     为什么必须服务端分页而不是前端切片：生产 849 位候选人单次响应约 950KB，
     浏览器要先下载并解析完这 950KB 才轮得到切片，滚动卡顿的根因在传输与渲染两侧，
     前端切片只解决后者。所以 page/pageSize 直接下推到后端聚合。
     为什么总数要单独存 total：看板各列要显示「该阶段共 N 人」，
     若用当前页 items 去数，翻到第 2 页每列数字都会变成 0，用户会以为数据丢了。 */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // 看板「不合适」列折叠状态（默认折叠，见 PAGE_SIZES 上方的注释）
  const [rejOpen, setRejOpen] = useState(false);
  /* Google 日历连接状态（HRMS 走查一-2 二期）。
     {configured, connected, email} 由 GET /gcal/status 下发。
    configured=false 表示服务端没配 GOOGLE_CLIENT_ID/SECRET —— 此时**整块入口隐藏**，
    让用户走「加入日历」的手动路径，而不是给一个点了必然报错的按钮。
    connected=true 后，安排面试会尝试创建日历事件并邀请候选人。 */
  const [gcal, setGcal] = useState(null);
  // 候选人搜索关键词。需求来源（HR 反馈 2026-10-14 · Recruiting ATS）：
  //   "Add a search bar for candidates to find a specific candidate through name or applied position."
  // 原先只过滤当前服务端分页里的卡片，候选人在别页时会显示为零匹配；现把关键词传给后端，
  // 在分页前按授权范围内的姓名 / 应聘职位等字段搜索，输入变化时回到第一页。
  const [cq, setCq] = useState("");
  const [debouncedCq, setDebouncedCq] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCq(cq.trim()), 250);
    return () => clearTimeout(timer);
  }, [cq]);
  const [focusCandidateId, setFocusCandidateId] = useState("");
  const [stageCounts, setStageCounts] = useState(null);
  const [savedCandidateNotice, setSavedCandidateNotice] = useState(null);
  // reload 可从创建 / AI 评分等异步回调触发。读 ref 中最新筛选条件，避免旧闭包用旧职位 / 页码
  // 发起请求；序号另用于忽略快速输入后晚到的旧响应，防止旧结果覆盖新搜索。
  const candidateListParams = useRef({});
  const candidateListRequest = useRef(0);
  const [roles, setRoles] = useState([]);
  const [branches, setBranches] = useState([]);
  const brOpts = scope ? branches.filter((b) => b.id === scope) : branches;
  const [searchParams] = useSearchParams();
  const [fJob, setFJob] = useState(searchParams.get("job") || "");
  // 所有筛选状态都已初始化后再同步 ref，避免首轮渲染访问暂时性死区中的 fJob。
  candidateListParams.current = { jobId: fJob, page, pageSize, q: debouncedCq, focusId: focusCandidateId, boardSort: true };
  const [jobDlg, setJobDlg] = useState(null);
  const [candDlg, setCandDlg] = useState(null);
  const [detail, setDetail] = useState(null);
  const [hireDlg, setHireDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState("board");
  const views = VIEWS.filter((v) => featVis(`/hrms/recruiting::${v.k}`, true));
  useEffect(() => { if (views.length && !views.some((v) => v.k === view)) setView(views[0].k); }, [views.map((v) => v.k).join(","), view]); // eslint-disable-line
  const [ana, setAna] = useState(null);
  const [intForm, setIntForm] = useState(null);
  const [fbForm, setFbForm] = useState(null);
  const [hrUsers, setHrUsers] = useState([]);
  const [offerDlg, setOfferDlg] = useState(null);
  const [parsing, setParsing] = useState(false);
  const fileRef = useRef(null);
  const [recDlg, setRecDlg] = useState(null);
  const [recBusy, setRecBusy] = useState("");
  const [pstats, setPstats] = useState(null);
  useEffect(() => {
    if (view === "analytics" && pstats === null) {
      api.get("/hrms/recruiting/portal-stats").then(({ data }) => setPstats(data)).catch(() => setPstats([]));
    }
  }, [api, view, pstats]);
  const [docsDlg, setDocsDlg] = useState(null);
  const [docTitle, setDocTitle] = useState("");
  const docFileRef = useRef(null);

  const openDocsDlg = async () => {
    try { const { data } = await api.get("/hrms/esign/docs"); setDocsDlg(data); } catch { toast.error(T("加载失败", "Load failed")); }
  };
  const addDocItem = async () => {
    if (!docTitle.trim()) return toast.error(T("请填写名称", "Name required"));
    try { await api.post("/hrms/esign/docs", { title: docTitle }); setDocTitle(""); openDocsDlg(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("添加失败", "Add failed")); }
  };
  const uploadDocPdf = async (f) => {
    const fd = new FormData();
    fd.append("file", f);
    fd.append("title", f.name.replace(/\.pdf$/i, ""));
    try {
      await api.post("/hrms/esign/docs/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(T("PDF 模板已上传，候选人签署时可在线查阅", "PDF template uploaded — viewable during e-sign")); openDocsDlg();
    } catch (e) { toast.error(e?.response?.data?.detail || T("上传失败", "Upload failed")); }
  };
  const delDocItem = async (d) => {
    try { await api.delete(`/hrms/esign/docs/${d.id}`); openDocsDlg(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("删除失败", "Delete failed")); }
  };
  const copyPortal = async () => {
    const url = `${window.location.origin}/careers`;
    try { await navigator.clipboard.writeText(url); toast.success(T("招聘门户链接已复制，可发布到官网/招聘平台", "Careers portal link copied")); }
    catch { toast.success(url); }
  };
  const downloadResumeFile = async () => {
    try {
      const res = await api.get(`/hrms/candidates/${detail.id}/resume-file`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error(T("下载失败", "Download failed")); }
  };
  const shareJob = async (j) => {
    const url = `${window.location.origin}/api/careers/share/${j.id}`;
    try { await navigator.clipboard.writeText(url); toast.success(T(`「${j.title}」分享链接已复制（微信/社交平台可显示职位预览标题）`, `"${j.title}" share link copied (with social preview)`)); }
    catch { toast.success(url); }
  };
  const downloadPoster = async (j) => {
    try {
      const res = await api.get(`/hrms/jobs/${j.id}/poster`, { params: { base: window.location.origin }, responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = `${T("招聘海报", "JobPoster")}_${j.title}.pdf`; a.click();
      URL.revokeObjectURL(a.href);
      toast.success(T("招聘海报已生成（含扫码投递二维码），可打印张贴", "Job poster generated (with QR apply code)"));
    } catch { toast.error(T("海报生成失败", "Poster generation failed")); }
  };
  const [poolQ, setPoolQ] = useState("");
  const [poolHits, setPoolHits] = useState(null);
  const [poolBusy, setPoolBusy] = useState(false);
  const searchPool = async () => {
    if (!poolQ.trim()) { setPoolHits(null); return; }
    setPoolBusy(true);
    try {
      const { data } = await api.get("/hrms/candidates", { params: { stage: "rejected", q: poolQ.trim() } });
      setPoolHits(data);
    } catch { toast.error(T("搜索失败", "Search failed")); }
    setPoolBusy(false);
  };
  const snippet = (c) => {
    const t = c.resumeText || "";
    const i = t.toLowerCase().indexOf(poolQ.trim().toLowerCase());
    if (i < 0) return (c.aiSummary || "").slice(0, 70);
    return (i > 30 ? "…" : "") + t.slice(Math.max(0, i - 30), i + poolQ.length + 55).replace(/\s+/g, " ") + "…";
  };
  const downloadIcs = async (iv) => {
    try {
      const res = await api.get(`/hrms/candidates/${detail.id}/interviews/${iv.id}/ics`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `interview_${detail.name}_${fmtD(iv.date)}.ics`; a.click();
      URL.revokeObjectURL(url);
      toast.success(T("日历邀请已下载，双击 .ics 文件即可加入日程", "Calendar invite downloaded (.ics)"));
    } catch { toast.error(T("下载失败", "Download failed")); }
  };
  /* 日历模板只预填候选人：内部面试官由系统站内提醒，打开模板不等于已发送邀请。 */
  const [calBusy, setCalBusy] = useState("");
  const openCalendarLink = async (iv) => {
    setCalBusy(iv.id);
    try {
      const { data } = await api.get(`/hrms/candidates/${detail.id}/interviews/${iv.id}/calendar-link`);
      if (!data?.url) return toast.error(T("暂时无法生成日历链接", "Calendar link unavailable"));
      window.open(data.url, "_blank", "noopener,noreferrer");
      if (!data.hasAttendees) {
        toast.message(T("已打开日历模板，但缺少候选人邮箱；请添加邮箱并在 Google 日历中保存、发送邀请",
          "Calendar template opened without a candidate email. Add it, then save and send the invitation in Google Calendar."));
      } else {
        toast.message(T("已打开 Google 日历模板；请在 Google 日历中保存并发送候选人邀请",
          "Google Calendar template opened. Save and send the candidate invitation in Google Calendar."));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("打开日历失败", "Failed to open calendar"));
    } finally {
      setCalBusy("");
    }
  };
  const [tagDlg, setTagDlg] = useState(null);
  const [tagBusy, setTagBusy] = useState(false);
  const saveTags = async () => {
    setTagBusy(true);
    try {
      await api.put(`/hrms/candidates/${tagDlg.id}/tags`, { tags: tagDlg.tags });
      toast.success(T("技能标签已保存", "Tags saved")); setTagDlg(null); loadAna();
      if (poolHits) searchPool();
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setTagBusy(false);
  };
  const addTag = () => {
    const v = (tagDlg.input || "").trim();
    if (!v) return;
    if (!tagDlg.tags.includes(v)) setTagDlg((d) => ({ ...d, tags: [...d.tags, v], input: "" }));
    else setTagDlg((d) => ({ ...d, input: "" }));
  };

  const aiRecommend = async (j) => {
    setRecBusy(j.id);
    try {
      const { data } = await api.post(`/hrms/jobs/${j.id}/ai-recommend`);
      setRecDlg({ job: j, ...data });
    } catch (e) { toast.error(e?.response?.data?.detail || T("AI 推荐失败", "AI recommend failed")); }
    setRecBusy("");
  };
  const requestEsign = async () => {
    try {
      const { data } = await api.post(`/hrms/candidates/${detail.id}/esign-request`);
      const url = `${window.location.origin}/esign/offer/${data.token}`;
      try { await navigator.clipboard.writeText(url); toast.success(T("电子签链接已生成并复制，发给候选人即可在线签署", "E-sign link generated & copied")); }
      catch { toast.success(T("电子签链接已生成：", "E-sign link: ") + url); }
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("生成失败", "Generation failed")); }
  };

  const loadAna = useCallback(() => {
    api.get("/hrms/recruiting/analytics").then(({ data }) => setAna(data)).catch(() => setAna(null));
  }, [api]);
  useEffect(() => { if (view !== "board") loadAna(); }, [view, loadAna]);

  const reload = useCallback(() => {
    // 修复（HR 反馈 2026-10-14 · Recruiting ATS）：
    //   原文 "Occasionally the records for the ATS disappears and requires refresh to load."
    //   原实现把「加载失败」直接 `.catch(() => setCands([]))` —— 一旦候选人接口偶发失败
    //   （网络抖动 / 后端聚合较慢被中断 / 网关超时），列表就被清空成空态；
    //   用户看到的是「记录突然消失」，刷新一次又回来，与反馈现象完全一致。
    // 最终效果：请求失败时保留既有列表（首次才落空数组），只提示不抹数据。
    // 关键取舍：不把「请求抖动」当成「数据为空」，避免用户误判记录丢失；
    //   代价是失败时看的是稍旧的一份数据，但可刷新重试，比清空更接近真实状态。
    api.get("/hrms/jobs").then(({ data }) => setJobs(data)).catch(() => setJobs((j) => j || []));
    // 分页（一-1）：带 page/pageSize 时后端返回 {items,total,page,totalPages,hasMore}。
    // 兼容处理：万一后端还是老版本（返回数组），仍能正常渲染，不至于白屏。
    const requestId = ++candidateListRequest.current;
    const filters = candidateListParams.current;
    if (filters.focusId) {
      setSavedCandidateNotice((notice) => (
        notice?.id === filters.focusId && notice.boardAvailable && ["not_found", "load_failed"].includes(notice.status)
          ? { ...notice, status: "loading" }
          : notice
      ));
    }
    api.get("/hrms/candidates", { params: {
      jobId: filters.jobId, page: filters.page, pageSize: filters.pageSize,
      q: filters.q, searchMode: "identity", boardSort: filters.boardSort,
      ...(filters.focusId ? { focusId: filters.focusId } : {}),
    } }).then(({ data }) => {
      if (requestId !== candidateListRequest.current) return;
      const responseItems = Array.isArray(data) ? data : (data?.items || []);
      // 老候选人可能没有 stage；后端阶段计数已按 new 汇总，前端卡片也要归到同一列。
      const items = responseItems.map((candidate) => (candidate.stage ? candidate : { ...candidate, stage: "new" }));
      setCands(items);
      setTotal(Array.isArray(data) ? items.length : (data?.total || 0));
      setTotalPages(Array.isArray(data) ? 1 : (data?.totalPages || 1));
      setStageCounts(Array.isArray(data) ? null : (data?.stageCounts || null));
      if (!Array.isArray(data) && Number.isFinite(data?.page)) setPage(data.page);
      setDetail((d) => (d ? items.find((c) => c.id === d.id) || d : d));
      if (filters.focusId) {
        const focusedCandidateReturned = items.some((candidate) => candidate.id === filters.focusId);
        setSavedCandidateNotice((notice) => (
          notice?.id === filters.focusId && notice.boardAvailable
            ? { ...notice, status: focusedCandidateReturned ? "located" : "not_found", refreshFailed: false }
            : notice
        ));
      }
    }).catch(() => {
      if (requestId === candidateListRequest.current) {
        toast.error(T("候选人列表加载失败，请稍后重试", "Failed to load candidates — please retry"));
        if (filters.focusId) {
          setSavedCandidateNotice((notice) => {
            if (notice?.id !== filters.focusId || !notice.boardAvailable) return notice;
            return notice.status === "located"
              ? { ...notice, refreshFailed: true }
              : { ...notice, status: "load_failed", refreshFailed: false };
          });
        }
      }
    });
  }, [api]);
  useEffect(() => {
    reload();
    return () => { candidateListRequest.current += 1; };
  }, [reload, fJob, page, pageSize, debouncedCq, focusCandidateId]);
  useEffect(() => {
    api.get("/hrms/role-catalog").then(({ data }) => setRoles(data)).catch(() => {});
    api.get("/branches").then(({ data }) => setBranches(data)).catch(() => {});
    api.get("/users").then(({ data }) => setHrUsers((data || []).filter((u) => ["admin", "ceo", "hr"].includes(u.role) && !u.disabled))).catch(() => setHrUsers([]));
    // Google 日历连接状态（走查一-2 二期）。失败不提示 —— 这是可选增强，连不上不影响招聘主流程。
    api.get("/gcal/status").then(({ data }) => setGcal(data)).catch(() => setGcal({ configured: false, connected: false }));
  }, [api]);

  /* 连接 / 断开 Google 日历（走查一-2 二期）。
     连接走 /gcal/auth-url 拿授权地址后整页跳转（OAuth 必须在 Google 域内完成跳转）；
     回调由后端 /api/public/gcal/callback 接住，成功后重定向前端 ?gcal=connected。
     不在这里轮询状态：授权是整页跳转，回来时页面会重新挂载并重新拉 status。 */
  const connectGcal = async () => {
    try {
      const { data } = await api.post("/gcal/auth-url");
      if (data?.url) window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("Google 日历尚未配置", "Google Calendar not configured"));
    }
  };
  const disconnectGcal = async () => {
    try {
      await api.post("/gcal/disconnect");
      setGcal((g) => ({ ...(g || {}), connected: false, email: "" }));
      toast.success(T("已断开 Google 日历", "Google Calendar unlinked"));
    } catch { toast.error(T("断开失败", "Unlink failed")); }
  };

  const saveJob = async () => {
    if (!jobDlg.form.title.trim()) return toast.error(T("请填写职位名称", "Job title required"));
    setBusy(true);
    try {
      if (jobDlg.id) await api.put(`/hrms/jobs/${jobDlg.id}`, jobDlg.form);
      else await api.post("/hrms/jobs", jobDlg.form);
      toast.success(T("职位已保存", "Job saved")); setJobDlg(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setBusy(false);
  };
  const delJob = async (j) => {
    /* 删除职位（HRMS 走查一-3 的连带修复）。
       后端已改成「有候选人时默认 409 拒绝」，这里做两段式确认：
         · 职位下没有候选人 → 一次确认即可；
         · 有候选人 → 明确报出条数并要求二次确认，用户同意后才带 force=true 重发。
       为什么不在第一次就问「要不要连候选人一起删」：多数人点删除只是想清理一个废弃职位，
       把「会删掉 153 份简历」做成默认选项太危险；先让他看到 409 的说明，再决定是否强删。
       文案必须报出真实条数 —— 只说「其下候选人也会删除」，用户并不知道规模有多大。 */
    const n = j.candidates || 0;
    if (!n) {
      if (!window.confirm(T(`删除职位「${j.title}」？`, `Delete job "${j.title}"?`))) return;
      try { await api.delete(`/hrms/jobs/${j.id}`); toast.success(T("已删除", "Deleted")); }
      catch (e) { toast.error(e?.response?.data?.detail || T("删除失败", "Delete failed")); }
      return;
    }
    const ok = window.confirm(T(
      `职位「${j.title}」下还有 ${n} 位候选人。\n\n继续删除会把这条职位和这 ${n} 位候选人的记录一起删除，且无法恢复。\n\n如果只是想改职位名称，请点「编辑」直接修改职位，不要删除。\n\n确认仍要删除？`,
      `Job "${j.title}" still has ${n} candidate(s).\n\nDeleting removes the job and all ${n} candidate records — this cannot be undone.\n\nIf you only need to rename it, use Edit instead.\n\nDelete anyway?`));
    if (!ok) return;
    try { await api.delete(`/hrms/jobs/${j.id}`, { params: { force: true } }); toast.success(T(`已删除职位及其 ${n} 位候选人`, `Deleted job and ${n} candidate(s)`)); }
    catch (e) { toast.error(e?.response?.data?.detail || T("删除失败", "Delete failed")); }
    reload();
  };
  const saveCand = async () => {
    // 编辑模式判定。需求来源（HR 反馈 2026-10-14 · Recruiting ATS）：
    //   "When editing candidate, it does not allow us to change or update candidate information
    //    without having to remake another one. Want to have a candidate information editing ability."
    //   原先前端只有「新建候选人」，详情页没有编辑入口，改个电话/薪资都要删掉重建。
    // 效果：带 id 时走 PUT /hrms/candidates/{id} 更新；不带 id 时维持原「入库 + 异步 AI 评分」流程。
    // 取舍：更新模式不再触发 AI 重评分（避免改个电话就重跑一次大模型），如需重评可在详情页手动触发。
    const editing = !!candDlg.id;
    if (!editing && !candDlg.jobId) return toast.error(T("请选择职位", "Select a job"));
    if (!candDlg.name.trim()) return toast.error(T("请填写姓名", "Name required"));
    setBusy(true);
    // 修复（2026-10-13，客户线上报障「点保存后按钮一直转圈、弹窗不关」）：
    // 原实现把「AI 评分」`await` 在保存链路里，且要到评分结束才 setCandDlg(null)；
    // AI 评分是外部大模型调用（ai_service._chat 未设超时），上游一慢或挂起，
    // 弹窗与保存按钮就会无限卡住 —— 候选人其实早已入库，用户却以为没保存成功。
    // 现改为：候选人入库成功即刻关闭弹窗、刷新列表；AI 评分转为后台异步执行，
    // 成功/失败各自给 toast，并在完成后二次刷新列表（评分结果需回填到卡片上）。
    // 取舍：不再「等评分完再反馈」，用户会先看到候选人进列表、几秒后看到 AI 分出现，
    // 体验更符合直觉，也彻底消除了因外部服务导致的界面卡死。
    const resumeText = (candDlg.resumeText || "").trim();
    try {
      if (editing) {
        await api.put(`/hrms/candidates/${candDlg.id}`, candDlg);
        toast.success(T("候选人信息已更新", "Candidate updated"));
        setCandDlg(null);
        reload();
        setBusy(false);
        return;
      }
      const { data } = await api.post("/hrms/candidates", candDlg);
      const savedName = candDlg.name.trim();
      const savedJobId = candDlg.jobId;
      const boardAvailable = views.some((v) => v.k === "board");
      // 保存成功就把看板切到该职位第一页，并用新候选人的唯一 id 暂时置顶。
      // 这既解除旧职位 / 第 N 页对新记录的遮挡，也避免后台 AI 评分改写排序后卡片从首屏跳走。
      // 同步 ref 是为了评分请求若很快返回，也能按刚保存的筛选条件刷新，而不是读到旧闭包状态。
      candidateListParams.current = { jobId: savedJobId, page: 1, pageSize, q: savedName, focusId: data.id, boardSort: true };
      setFJob(savedJobId);
      setPage(1);
      setCq(savedName);
      setDebouncedCq(savedName);
      setFocusCandidateId(data.id);
      setSavedCandidateNotice({
        id: data.id, name: savedName, jobTitle: jobs?.find((job) => job.id === savedJobId)?.title || "",
        boardAvailable, status: boardAvailable ? "loading" : "no_board",
      });
      if (boardAvailable) setView("board");
      setCandDlg(null);
      toast.success(T("候选人已保存", "Candidate saved"));
      if (resumeText) {
        toast.info(T("AI 评分中…", "AI scoring…"));
        api.post(`/hrms/candidates/${data.id}/ai-score`)
          .then(() => {
            toast.success(T("AI 评分完成", "AI scoring done"));
            reload(); if (view !== "board") loadAna();
          })
          .catch(() => toast.error(T("AI 评分失败，可稍后在详情中重试", "AI scoring failed — retry in detail view")));
      }
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setBusy(false);
  };
  const aiScore = async (c) => {
    setBusy(true);
    try { await api.post(`/hrms/candidates/${c.id}/ai-score`); toast.success(T("AI 评分完成", "AI scoring done")); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("AI 评分失败", "AI scoring failed")); }
    setBusy(false);
  };
  const moveStage = async (c, stage) => {
    try { await api.post(`/hrms/candidates/${c.id}/stage`, { stage }); toast.success(T(`已移至「${STAGES[stage].label}」`, `Moved to "${STAGES[stage].en}"`)); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || T("操作失败", "Action failed")); }
  };
  const hire = async () => {
    setBusy(true);
    try {
      await api.post(`/hrms/candidates/${hireDlg.cand.id}/hire`, {
        hireDate: hireDlg.hireDate, branch: hireDlg.branch,
        salary: hireDlg.salary || 0, salaryType: hireDlg.salaryType || "monthly",
      });
      toast.success(T("已录用：员工档案已建立，入职流程已发起" + (hireDlg.salary ? "，薪酬已写入档案" : ""), "Hired: employee record created & onboarding started" + (hireDlg.salary ? ", salary saved" : "")));
      setHireDlg(null); setDetail(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("录用失败", "Hire failed")); }
    setBusy(false);
  };
  const saveOffer = async () => {
    if (!offerDlg.salary) return toast.error(T("请填写薪资", "Salary required"));
    setBusy(true);
    try {
      await api.post(`/hrms/candidates/${detail.id}/offer`, offerDlg);
      toast.success(T("Offer 已保存，可下载 Offer 信函 PDF", "Offer saved — letter PDF available"));
      setOfferDlg(null); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setBusy(false);
  };
  const downloadOffer = async (c) => {
    try {
      const res = await api.get(`/hrms/candidates/${c.id}/offer-letter`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Offer_${c.name}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(T("Offer 信函已下载", "Offer letter downloaded"));
    } catch (e) { toast.error(T("下载失败，请先设置 Offer", "Download failed — set the offer first")); }
  };
  const delCand = async (c) => {
    if (!window.confirm(T(`删除候选人「${c.name}」？`, `Delete candidate "${c.name}"?`))) return;
    try { await api.delete(`/hrms/candidates/${c.id}`); setDetail(null); reload(); } catch (e) { toast.error(T("删除失败", "Delete failed")); }
  };
  const addInterview = async () => {
    if (!intForm.date) return toast.error(T("请选择面试日期", "Pick an interview date"));
    setBusy(true);
    try {
      const { data } = await api.post(`/hrms/candidates/${detail.id}/interviews`, intForm);
      // 面试保存、内部提醒和候选人邀请是独立结果；Google 接受请求也不等于对方已收到。
      const reminder = data?.reminder?.status;
      const calendar = data?.calendar;
      const g = data?.gcal;
      toast.success(T("面试已安排", "Interview scheduled"));
      if (reminder === "sent") toast.message(T("已创建面试官系统提醒", "An in-app notification was created for the interviewer."));
      else if (reminder === "not_found") toast.warning(T("未找到内部面试官账号，未发送系统提醒", "Interviewer account not found; no in-app notification was sent."));
      else if (reminder === "failed") toast.warning(T("面试已保存，但系统提醒创建失败，请另行通知面试官", "Interview saved, but the in-app notification failed. Notify the interviewer separately."));
      if (calendar?.status === "created_unrecorded") {
        toast.warning(T("Google 日历事件已创建，但系统未能记录结果；请先检查日历，避免重复创建", "Google Calendar event created, but the result could not be recorded. Check the calendar before retrying."));
      }
      if (calendar?.candidateStatus === "missing" || calendar?.candidateStatus === "invalid") {
        toast.warning(T("候选人邮箱缺失或无效，未发送候选人日历邀请", "Candidate email is missing or invalid; no calendar invitation was sent."));
      } else if (calendar?.candidateStatus === "same_as_organizer" || (calendar?.status === "pushed" && !g?.attendeeCount)) {
        toast.warning(T("候选人未被加入日历邀请，请核对候选人邮箱与排期人账号", "The candidate was not included in the calendar invitation. Check the candidate email and organizer account."));
      } else if (calendar?.status === "pushed" || (!calendar && g?.pushed && g.attendeeCount > 0)) {
        toast.message(T("Google 日历已提交候选人邀请，请以对方收到情况为准", "Candidate invitation submitted to Google Calendar; delivery is not guaranteed."));
      } else if (calendar?.status === "not_configured") {
        toast.warning(T("Google 日历尚未配置，候选人未收到自动邀请", "Google Calendar is not configured; no automatic candidate invitation was sent."));
      } else if (calendar?.status === "not_connected") {
        toast.warning(T("你尚未连接 Google 日历，候选人未收到自动邀请", "Your Google Calendar is not connected; no automatic candidate invitation was sent."));
      } else if (calendar?.status === "failed" || (!calendar && g?.pushed === false)) {
        toast.warning(T("日历邀请未能提交，请核对后通过「加入日历」手动处理", "Calendar invitation could not be submitted. Check and use Add to Calendar if needed."));
      }
      setIntForm(null); reload(); if (view !== "board") loadAna();
    } catch (e) { toast.error(e?.response?.data?.detail || T("安排失败", "Scheduling failed")); }
    setBusy(false);
  };
  const saveFeedback = async () => {
    setBusy(true);
    try {
      await api.put(`/hrms/candidates/${fbForm.candId}/interviews/${fbForm.id}`, fbForm);
      toast.success(T("面试评价已记录", "Feedback saved")); setFbForm(null); reload(); if (view !== "board") loadAna();
    } catch (e) { toast.error(e?.response?.data?.detail || T("保存失败", "Save failed")); }
    setBusy(false);
  };
  const reactivate = async (c) => {
    try {
      await api.post(`/hrms/candidates/${c.id}/stage`, { stage: "new", note: T("人才库二次激活", "Reactivated from talent pool") });
      toast.success(T(`「${c.name}」已重新激活至管道`, `"${c.name}" reactivated into pipeline`)); loadAna(); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || T("操作失败", "Action failed")); }
  };

  if (!canView) return <div className="p-8 text-center text-slate-400 text-sm">{T("仅 HR / 管理层 / 部门经理可访问招聘管理", "Recruiting is HR / management / dept manager only")}</div>;
  return (
    <div className="space-y-4" data-testid="hrms-recruiting-page">
      <div className="flex flex-wrap items-center gap-3">
        <div>
          <h1 className="text-xl font-extrabold text-slate-800">{T("招聘 ATS", "Recruiting ATS")}</h1>
          <p className="text-xs text-slate-400 mt-0.5">{T("职位发布 → 候选人管道 → AI 简历评分（按岗位 JD）→ 录用一键建档 + 入职编排", "Job posting → candidate pipeline → AI resume scoring (per JD) → one-click hire + onboarding")}</p>
        </div>
        <ScopeBadge scope={scope} branches={branches} zh={zh} />
        {canManage && (
          <div className="ml-auto flex gap-2 flex-wrap">
            <button data-testid="hrms-portal-link-btn" onClick={copyPortal}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-semibold px-4 py-2"><Link2 className="h-3.5 w-3.5" />{T("门户链接", "Portal link")}</button>
            <button data-testid="hrms-esign-docs-btn" onClick={openDocsDlg}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-semibold px-4 py-2"><FileStack className="h-3.5 w-3.5" />{T("入职文件包", "Onboarding docs")}</button>
            {/* Google 日历连接状态（HRMS 走查一-2 二期）。
                只在**服务端已配置凭据**时才显示 —— 没配 GOOGLE_CLIENT_ID/SECRET 时
                显示一个点了就报错的按钮只会制造困惑，所以整块隐藏，用户走「加入日历」手动路径。
                连上之后，排面试会尝试写进操作人的日历并邀请候选人。 */}
            {gcal && gcal.configured && (
              gcal.connected ? (
                <span data-testid="hrms-gcal-connected" title={gcal.email || ""}
                  className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold px-4 py-2">
                  <CalendarClock className="h-3.5 w-3.5" />{T("日历已连接", "Calendar linked")}
                  <button onClick={disconnectGcal} className="ml-1 text-emerald-600 hover:text-red-500 underline">{T("断开", "Unlink")}</button>
                </span>
              ) : (
                <button data-testid="hrms-gcal-connect-btn" onClick={connectGcal}
                  className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-semibold px-4 py-2"
                  title={T("连接后，安排面试会尝试写进你的 Google 日历并邀请候选人；面试官使用系统提醒", "Once linked, interviews are added to your Google Calendar and candidates are invited; interviewers receive in-app notifications.")}>
                  <CalendarClock className="h-3.5 w-3.5" />{T("连接 Google 日历", "Link Google Calendar")}
                </button>
              )
            )}
            <button data-testid="hrms-job-create-btn" onClick={() => setJobDlg({ form: { ...EMPTY_JOB, branch: scope || EMPTY_JOB.branch } })}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 text-xs font-semibold px-4 py-2"><Briefcase className="h-3.5 w-3.5" />{T("发布职位", "Post job")}</button>
            <button data-testid="hrms-cand-create-btn" onClick={() => setCandDlg({ ...EMPTY_CAND, jobId: fJob || (jobs?.[0]?.id || "") })}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 rounded-full bg-brand-500 hover:bg-brand-600 text-white text-xs font-semibold px-4 py-2"><UserPlus className="h-3.5 w-3.5" />{T("添加候选人", "Add candidate")}</button>
          </div>
        )}
      </div>

      <div className="flex gap-1.5 flex-wrap">
        {views.map((v) => (
          <button key={v.k} data-testid={`hrms-rec-view-${v.k}`} onClick={() => setView(v.k)}
            className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full font-semibold ${view === v.k ? "bg-slate-800 text-white" : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
            <v.icon className="h-3.5 w-3.5" />{zh ? v.label : v.en}
          </button>
        ))}
      </div>

      {savedCandidateNotice && (
        <div role="status" data-testid="hrms-cand-save-notice" className="flex items-start justify-between gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          <div className="min-w-0">
            <p className="min-w-0 flex flex-wrap items-center gap-1.5 font-semibold break-words"><CheckCircle2 aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />{T("候选人「" + savedCandidateNotice.name + "」已保存" + (savedCandidateNotice.jobTitle ? "，应聘职位：" + savedCandidateNotice.jobTitle : ""), "Candidate \"" + savedCandidateNotice.name + "\" was saved" + (savedCandidateNotice.jobTitle ? " for " + savedCandidateNotice.jobTitle : ""))}</p>
            {savedCandidateNotice.boardAvailable
              ? <p className="mt-0.5">{({
                loading: T("正在确认看板列表已返回这位候选人…", "Checking that the board list returned this candidate…"),
                located: T("已在该职位看板第 1 页的列表中找到。", "Found on page 1 of the board for this job."),
                not_found: T("已保存，但列表响应中没有这条记录；请刷新后重试。", "Candidate saved, but the list did not return it. Refresh and retry."),
                load_failed: T("已保存，但候选人列表加载失败；请刷新后重试。", "Candidate saved, but the list failed to load. Refresh and retry."),
              })[savedCandidateNotice.status] || T("正在确认看板列表已返回这位候选人…", "Checking that the board list returned this candidate…")}</p>
              : <p className="mt-0.5">{T("当前账号没有招聘看板栏目权限；如需查看候选人卡片，请联系管理员配置该栏目权限。", "This account cannot view the recruiting board. Ask an administrator to grant access to that section if you need to view the candidate card.")}</p>}
            {savedCandidateNotice.refreshFailed && savedCandidateNotice.status === "located" && (
              <p className="mt-0.5">{T("候选人先前已在列表中找到，但本次列表刷新失败；当前列表已保留，请稍后重试。", "This candidate was previously found, but the latest list refresh failed. The current list was kept; please retry later.")}</p>
            )}
          </div>
          <button type="button" aria-label={T("关闭保存提示", "Dismiss save notice")} onClick={() => setSavedCandidateNotice(null)} className="min-h-11 min-w-11 shrink-0 rounded p-1 text-emerald-700 hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-700 focus-visible:ring-offset-1">
            <X aria-hidden="true" className="mx-auto h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {view === "board" && (<>
      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <ResponsiveTable className="w-full text-sm">
          <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
            <th className="px-3 py-2">{T("职位", "Job")}</th><th className="px-3 py-2">{T("法人", "Entity")}</th><th className="px-3 py-2">{T("名额", "Openings")}</th>
            <th className="px-3 py-2">{T("候选人", "Candidates")}</th><th className="px-3 py-2">{T("已录用", "Hired")}</th><th className="px-3 py-2">{T("状态", "Status")}</th><th className="px-3 py-2 text-right">{T("操作", "Actions")}</th>
          </tr></thead>
          <tbody>
            {jobs === null ? <tr><td colSpan={7} className="text-center py-6 text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr> :
              jobs.length === 0 ? <tr><td colSpan={7} className="text-center py-6 text-slate-300">{T("暂无职位", "No jobs")}{canManage ? T("，点击「发布职位」创建", " — click 'Post job' to create") : ""}</td></tr> :
              jobs.map((j) => (
                <tr key={j.id} className={`border-b border-slate-50 cursor-pointer ${fJob === j.id ? "bg-indigo-50/50" : "hover:bg-slate-50/60"}`}
                  data-testid={`hrms-job-row-${j.id}`} onClick={() => { setFJob(fJob === j.id ? "" : j.id); setPage(1); setDebouncedCq(cq.trim()); setFocusCandidateId(""); setSavedCandidateNotice(null); }}>
                  <td className="px-3 py-2 font-semibold text-slate-700">{j.title}<span className="text-slate-400 font-normal text-xs ml-1">{(() => { const r = roles.find((r) => r.role === j.role); return r ? (zh ? r.zh : (r.en || r.zh)) : ""; })()}</span></td>
                  <td className="px-3 py-2 text-slate-500 text-xs">{(() => { const b = branches.find((b) => b.id === j.branch); return b ? (zh ? b.zh : (b.en || b.zh)) : j.branch; })()}</td>
                  <td className="px-3 py-2">{j.openings}</td>
                  <td className="px-3 py-2">{j.candidates}</td>
                  <td className="px-3 py-2">{j.hired ? <span className="text-emerald-600 font-semibold">{j.hired}</span> : "-"}</td>
                  <td className="px-3 py-2"><span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${j.status === "open" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>{j.status === "open" ? T("招聘中", "Open") : T("已关闭", "Closed")}</span></td>
                  <td className="px-3 py-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                    {canManage && (
                      <>
                        <button data-testid={`hrms-job-poster-${j.id}`} onClick={() => downloadPoster(j)} title={T("生成扫码招聘海报 PDF", "Generate QR job poster PDF")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-violet-500 mr-2"><QrCode className="h-3.5 w-3.5" /></button>
                        <button data-testid={`hrms-job-share-${j.id}`} onClick={() => shareJob(j)} title={T("复制分享链接（含社交预览）", "Copy share link (social preview)")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-sky-500 mr-2"><Share2 className="h-3.5 w-3.5" /></button>
                        <button data-testid={`hrms-job-airec-${j.id}`} disabled={recBusy === j.id} onClick={() => aiRecommend(j)} title={T("AI 从人才库推荐", "AI recommend from talent pool")} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-amber-500 mr-2 disabled:opacity-50">{recBusy === j.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}</button>
                        <button data-testid={`hrms-job-edit-${j.id}`} onClick={() => setJobDlg({ id: j.id, form: { title: j.title, role: j.role, branch: j.branch, openings: j.openings, description: j.description, channels: j.channels, status: j.status } })} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-indigo-600 mr-2"><Pencil className="h-3.5 w-3.5" /></button>
                        <button data-testid={`hrms-job-del-${j.id}`} onClick={() => delJob(j)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
          </tbody>
        </ResponsiveTable>
        {fJob && <p className="px-3 py-1.5 text-[11px] text-indigo-600 bg-indigo-50/60">{T("已按职位过滤管道，再次点击该职位行取消过滤", "Pipeline filtered by job — click the row again to clear")}</p>}
      </div>

      {/* 候选人搜索栏（HR 反馈 2026-10-14 · Recruiting ATS）：
          姓名 / 应聘职位搜索由服务端在分页前执行；筛选范围和总匹配数因此覆盖所有页面。 */}
      <div className="flex flex-wrap items-center gap-2" data-testid="hrms-cand-search">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input data-testid="hrms-cand-search-input" className={`${inp} pl-8 pr-8`} value={cq}
            onChange={(e) => {
              const value = e.target.value;
              setCq(value); setPage(1); setFocusCandidateId("");
              setSavedCandidateNotice(null);
              if (!value.trim()) setDebouncedCq("");
            }}
            placeholder={T("搜索候选人：姓名 / 应聘职位", "Search: name / applied position")} />
          {cq && (
            <button data-testid="hrms-cand-search-clear" onClick={() => { setCq(""); setDebouncedCq(""); setPage(1); setFocusCandidateId(""); setSavedCandidateNotice(null); }}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        {debouncedCq && cq.trim() === debouncedCq && (
          <span className="text-[11px] text-slate-400" data-testid="hrms-cand-search-count">
            {T(`匹配 ${total} 人`, `${total} matched`)}
          </span>
        )}
      </div>

      {/* 候选人管道看板（HRMS 走查一-1）。
          两块改动：
            ① 「不合适」列默认折叠成一个窄条 —— 见 PAGE_SIZES 上方的注释（生产 816/849 是 rejected）；
            ② 底部加分页条（20/50/100），数据来自服务端分页。
          布局取舍：折叠态在 lg 下只占 1 列（与其他阶段同宽），不做特殊跨度 ——
          试过让它更窄，但 min-h 与折叠内容会把同一行其他列挤成不同高度，视觉更乱。
          移动端折叠态与普通列同宽，避免窄屏下出现只有几个字的畸形格子。 */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="hrms-pipeline">
        {Object.entries(STAGES).map(([k, st]) => {
          const list = cands.filter((c) => c.stage === k);
          // 服务端 stageCounts 统计当前授权 / 职位 / 关键词范围内的全量人数；卡片仍遵循全局分页。
          // 旧 API 数组响应没有该字段时，退回显示当前响应中的数量，保持旧版本兼容。
          const stageCount = stageCounts && Number.isFinite(stageCounts[k]) ? stageCounts[k] : list.length;
          // 折叠态：只显示标题与人数，点一下就展开
          if (k === "rejected" && !rejOpen) {
            return (
              <button key={k} onClick={() => setRejOpen(true)} data-testid="hrms-stage-col-rejected-collapsed"
                title={T("展开「不合适」列", "Expand Rejected column")}
                className="min-h-[180px] rounded-xl border border-slate-200 bg-slate-50/60 hover:bg-slate-100/80 flex flex-col items-center justify-start gap-2 py-3 transition-colors">
                <ChevronsUpDown className="h-4 w-4 text-slate-400" />
                <span className="text-[11px] font-bold text-slate-500">{zh ? st.label : st.en}</span>
                <span className="text-lg font-extrabold text-slate-400 tabular-nums" title={T("当前权限与筛选范围内的全量阶段人数", "Total candidates in this stage within your access and filters")}>{stageCount}</span>
                <span className="text-[10px] text-slate-400 text-center px-1 leading-tight">{T("点击展开", "Click to expand")}</span>
              </button>
            );
          }
          return (
            <div key={k} className={`rounded-xl border-t-4 ${st.cls} bg-white border border-slate-200 min-h-[180px]`} data-testid={`hrms-stage-col-${k}`}>
              <p className="px-2.5 py-2 text-xs font-bold text-slate-600 border-b border-slate-50 flex items-center gap-1">
                <span className="truncate">{zh ? st.label : st.en}</span>
                <span className="text-slate-400 font-normal" title={T("当前权限与筛选范围内的全量阶段人数", "Total candidates in this stage within your access and filters")}>{stageCount}</span>
                {k === "rejected" && (
                  <button onClick={() => setRejOpen(false)} data-testid="hrms-stage-col-rejected-collapse"
                    title={T("收起「不合适」列", "Collapse Rejected column")}
                    className="ml-auto min-h-6 min-w-6 text-slate-300 hover:text-slate-600"><ChevronsDownUp className="h-3.5 w-3.5" /></button>
                )}
              </p>
              <div className="p-1.5 space-y-1.5">
                {list.map((c) => (
                  <button key={c.id} data-testid={`hrms-cand-card-${c.id}`} onClick={() => setDetail(c)}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full text-left rounded-lg border border-slate-100 hover:border-indigo-300 px-2.5 py-2 bg-white">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-bold text-slate-700 truncate">{c.name}</span>
                      {scoreBadge(c.aiScore, zh)}
                    </div>
                    <p className="text-[10px] text-slate-400 mt-0.5 truncate">{c.jobTitle} · {SRC[c.source] || c.source}{c.expectedSalary ? ` · ${sym(c.jobId)}${c.expectedSalary}` : ""}</p>
                  </button>
                ))}
                {stageCount > list.length && (
                  <p data-testid={`hrms-stage-visibility-note-${k}`} className="px-2 py-1 text-[11px] leading-4 text-slate-500">
                    {list.length
                      ? T(`本页显示 ${list.length} 位，另有 ${stageCount - list.length} 位在其他页。请用下方分页查看。`, `Current page: ${list.length}. On other pages: ${stageCount - list.length}. Use the pager below.`)
                      : T(`本页暂无卡片，${stageCount} 位候选人在其他页。请用下方分页查看。`, `No cards on this page. ${stageCount} candidates are on other pages; use the pager below.`)}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* 分页条（一-1）：桌面紧凑、移动端按钮保持 44px 触控。
          注：这里的 total 是「按当前筛选（职位）后的总数」，不是全库总数 ——
          与上方职位行过滤联动，避免用户以为分页吃了数据。 */}
      {total > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2" data-testid="hrms-cand-pager">
          <div className="flex flex-col gap-1">
            <span className="text-[11px] text-slate-500">
              {T(`第 ${page} / ${totalPages} 页 · 共 ${total} 位候选人`, `Page ${page} / ${totalPages} · ${total} candidates`)}
            </span>
            <span className="text-[10px] text-slate-400">
              {T("列人数按当前权限、职位和关键词汇总；新阶段按最近入库优先，超出每页条数后续页继续显示。", "Counts reflect your access and filters. New-stage candidates appear first by recency and continue on later pages when they exceed the page size.")}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); setSavedCandidateNotice(null); }}
              data-testid="hrms-cand-pagesize"
              className="min-h-11 md:min-h-0 rounded-md border border-slate-200 px-1.5 py-1 text-[11px] bg-white">
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{T(`${n} 条/页`, `${n} / page`)}</option>)}
            </select>
            <button data-testid="hrms-cand-prev" disabled={page <= 1} onClick={() => { setSavedCandidateNotice(null); setFocusCandidateId(""); setPage(page - 1); }}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center justify-center rounded-md border border-slate-200 px-2 py-1 text-slate-600 disabled:opacity-40 hover:bg-slate-50">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button data-testid="hrms-cand-next" disabled={page >= totalPages} onClick={() => { setSavedCandidateNotice(null); setFocusCandidateId(""); setPage(page + 1); }}
              className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center justify-center rounded-md border border-slate-200 px-2 py-1 text-slate-600 disabled:opacity-40 hover:bg-slate-50">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
      </>)}

      {view === "interviews" && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="hrms-rec-interviews">
          <ResponsiveTable className="w-full text-sm">
            <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2">{T("日期/时间", "Date/Time")}</th><th className="px-3 py-2">{T("候选人", "Candidate")}</th><th className="px-3 py-2">{T("职位", "Job")}</th>
              <th className="px-3 py-2">{T("轮次/方式", "Round/Mode")}</th><th className="px-3 py-2">{T("面试官", "Interviewer")}</th><th className="px-3 py-2">{T("评分", "Score")}</th>
              <th className="px-3 py-2">{T("结论", "Decision")}</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {!ana ? <tr><td colSpan={8} className="text-center py-6 text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr> :
                ana.interviews.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-slate-300">{T("暂无面试安排，在候选人详情中点击「安排面试」", "No interviews — schedule one from a candidate's detail view")}</td></tr> :
                ana.interviews.map((iv) => (
                  <tr key={iv.id} className="border-b border-slate-50" data-testid={`hrms-iv-row-${iv.id}`}>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-700">{fmtD(iv.date)} {iv.time}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{iv.candName}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{iv.jobTitle}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{T(`第${iv.round}轮`, `Round ${iv.round}`)} · {IM[iv.mode]}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">{iv.interviewer || "-"}</td>
                    <td className="px-3 py-2 text-xs">{iv.score ? <b className="text-slate-700">{iv.score}/5</b> : "-"}</td>
                    <td className="px-3 py-2">{iv.decision ? <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold ${DEC_CLS[iv.decision]}`}>{DEC[iv.decision]}</span> : <span className="text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400">{T("待面试", "Pending")}</span>}</td>
                    <td className="px-3 py-2 text-right">
                      <button data-testid={`hrms-iv-fb-${iv.id}`} onClick={() => setFbForm({ candId: iv.candId, id: iv.id, candName: iv.candName, round: iv.round, score: iv.score || 3, decision: iv.decision || "pass", notes: iv.notes || "", strengths: iv.strengths || "", weaknesses: iv.weaknesses || "", conclusion: iv.conclusion || "" })}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] rounded-full border border-slate-300 text-slate-500 hover:bg-slate-50 font-semibold px-3 py-1">{iv.decision ? T("改评价", "Edit feedback") : T("记录评价", "Add feedback")}</button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </ResponsiveTable>
        </div>
      )}

      {view === "analytics" && (
        !ana ? <div className="p-8 text-center text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></div> : (
        <div className="space-y-3" data-testid="hrms-rec-analytics">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-ana-total">
              <p className="text-xs text-slate-500">{T("候选人总数", "Total candidates")}</p><p className="text-2xl font-extrabold text-slate-800 mt-1">{Object.values(ana.funnel).reduce((a, b) => a + b, 0)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-ana-hired">
              <p className="text-xs text-slate-500">{T("已录用", "Hired")}</p><p className="text-2xl font-extrabold text-emerald-600 mt-1">{ana.funnel.hired || 0}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-ana-days">
              <p className="text-xs text-slate-500">{T("平均招聘周期", "Avg time to hire")}</p><p className="text-2xl font-extrabold text-slate-800 mt-1">{ana.avgDaysToHire != null ? T(`${ana.avgDaysToHire} 天`, `${ana.avgDaysToHire} days`) : "—"}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-ana-pool">
              <p className="text-xs text-slate-500">{T("人才库沉淀", "Talent pool")}</p><p className="text-2xl font-extrabold text-slate-800 mt-1">{ana.pool.length}</p>
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="hrms-ana-funnel">
            <p className="text-xs font-bold text-slate-600 mb-2">{T("招聘漏斗", "Hiring Funnel")}</p>
            <div className="space-y-1.5">
              {Object.entries(STAGES).map(([k, st]) => {
                const max = Math.max(...Object.values(ana.funnel), 1);
                return (
                  <div key={k} className="flex items-center gap-2 text-xs">
                    <span className="w-16 text-slate-500">{zh ? st.label : st.en}</span>
                    <div className="flex-1 h-4 rounded bg-slate-50 overflow-hidden">
                      <div className={`h-full rounded ${k === "hired" ? "bg-emerald-400" : k === "rejected" ? "bg-slate-300" : "bg-indigo-400"}`} style={{ width: `${(ana.funnel[k] || 0) / max * 100}%` }} />
                    </div>
                    <b className="w-6 text-right text-slate-700">{ana.funnel[k] || 0}</b>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="hrms-portal-stats">
            <p className="px-4 py-2.5 border-b border-slate-100 text-xs font-bold text-slate-600">{T("门户投递统计（各职位浏览 → 投递转化 · 分享/海报效果）", "Portal application stats (views → applies · share/poster impact)")}</p>
            <ResponsiveTable className="w-full text-sm">
              <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
                <th className="px-3 py-2">{T("职位", "Job")}</th><th className="px-3 py-2">{T("状态", "Status")}</th><th className="px-3 py-2">{T("门户浏览", "Views")}</th>
                <th className="px-3 py-2">{T("分享点击", "Share hits")}</th><th className="px-3 py-2">{T("门户投递", "Applies")}</th><th className="px-3 py-2">{T("转化率", "Conversion")}</th>
              </tr></thead>
              <tbody>
                {pstats === null ? <tr><td colSpan={6} className="text-center py-6 text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr> :
                  pstats.length === 0 ? <tr><td colSpan={6} className="text-center py-6 text-slate-300">{T("暂无职位数据", "No job data")}</td></tr> :
                  pstats.map((p) => (
                    <tr key={p.id} className="border-b border-slate-50" data-testid={`hrms-portal-stat-${p.id}`}>
                      <td className="px-3 py-2 text-xs font-semibold text-slate-700">{p.title}</td>
                      <td className="px-3 py-2 text-xs">{p.status === "open" ? <span className="text-emerald-600">{T("在招", "Open")}</span> : <span className="text-slate-400">{T("已关闭", "Closed")}</span>}</td>
                      <td className="px-3 py-2 text-xs">{p.views}</td>
                      <td className="px-3 py-2 text-xs">{p.shareHits}</td>
                      <td className="px-3 py-2 text-xs font-bold text-indigo-600">{p.applies}</td>
                      <td className="px-3 py-2 text-xs">{p.conv != null ? `${p.conv}%` : "—"}</td>
                    </tr>
                  ))}
              </tbody>
            </ResponsiveTable>
          </div>
          <div className="grid lg:grid-cols-2 gap-3">
            <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="hrms-ana-sources">
              <p className="px-4 py-2.5 border-b border-slate-100 text-xs font-bold text-slate-600">{T("渠道效果（Indeed / ZipRecruiter / 内推 等）", "Source effectiveness (Indeed / ZipRecruiter / referrals…)")}</p>
              <ResponsiveTable className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100"><th className="px-3 py-2">{T("渠道", "Source")}</th><th className="px-3 py-2">{T("简历", "Resumes")}</th><th className="px-3 py-2">{T("进面试", "Interviewed")}</th><th className="px-3 py-2">{T("录用", "Hired")}</th><th className="px-3 py-2">{T("转化率", "Conversion")}</th></tr></thead>
                <tbody>
                  {Object.entries(ana.sources).sort((a, b) => b[1].total - a[1].total).map(([k, s]) => (
                    <tr key={k} className="border-b border-slate-50">
                      <td className="px-3 py-2 text-xs font-semibold text-slate-700">{SRC[k] || k}</td>
                      <td className="px-3 py-2 text-xs">{s.total}</td><td className="px-3 py-2 text-xs">{s.interviewed}</td>
                      <td className="px-3 py-2 text-xs text-emerald-600 font-bold">{s.hired}</td>
                      <td className="px-3 py-2 text-xs">{s.total ? Math.round(s.hired / s.total * 100) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </ResponsiveTable>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="hrms-ana-jobs">
              <p className="px-4 py-2.5 border-b border-slate-100 text-xs font-bold text-slate-600">{T("在招职位补齐进度", "Open positions fill progress")}</p>
              <ResponsiveTable className="w-full text-sm">
                <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100"><th className="px-3 py-2">{T("职位", "Job")}</th><th className="px-3 py-2">{T("候选人", "Candidates")}</th><th className="px-3 py-2">{T("录用/名额", "Hired/Openings")}</th><th className="px-3 py-2">{T("进度", "Progress")}</th></tr></thead>
                <tbody>
                  {ana.jobsFill.length === 0 ? <tr><td colSpan={4} className="text-center py-6 text-slate-300">{T("暂无在招职位", "No open jobs")}</td></tr> :
                    ana.jobsFill.map((j) => (
                      <tr key={j.id} className="border-b border-slate-50">
                        <td className="px-3 py-2 text-xs font-semibold text-slate-700">{j.title}</td>
                        <td className="px-3 py-2 text-xs">{j.candidates}</td>
                        <td className="px-3 py-2 text-xs">{j.hired}/{j.openings}</td>
                        <td className="px-3 py-2">
                          <div className="h-2 w-20 rounded-full bg-slate-100 overflow-hidden">
                            <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(100, j.hired / j.openings * 100)}%` }} />
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </ResponsiveTable>
            </div>
          </div>
        </div>
      ))}

      {view === "pool" && (
        <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto" data-testid="hrms-rec-pool">
          <div className="px-4 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-2">
            <p className="text-xs font-bold text-slate-600">{T("人才库 · 未录用候选人沉淀（可二次激活）", "Talent pool · non-hired candidates (reactivatable)")}</p>
            <div className="flex-wrap md:flex-nowrap ml-auto flex items-center gap-1.5">
              <input data-testid="hrms-pool-search" className="text-xs rounded-full border border-slate-200 px-3 py-1.5 w-52 focus:outline-none focus:ring-1 focus:ring-brand-400"
                value={poolQ} onChange={(e) => setPoolQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && searchPool()}
                placeholder={T("按技能关键词搜简历，如 PMP / 焊接…", "Search resumes by skill, e.g. PMP / welding…")} />
              <button data-testid="hrms-pool-search-btn" disabled={poolBusy} onClick={searchPool}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full bg-slate-800 hover:bg-slate-700 text-white font-semibold px-3 py-1.5 disabled:opacity-50">
                {poolBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Search className="h-3 w-3" />}{T("搜索", "Search")}
              </button>
              {poolHits && <button data-testid="hrms-pool-search-clear" onClick={() => { setPoolQ(""); setPoolHits(null); }} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-slate-400 hover:text-slate-600">{T("清除", "Clear")}</button>}
            </div>
          </div>
          {poolHits && <p className="px-4 py-1.5 text-[11px] text-slate-400 border-b border-slate-50" data-testid="hrms-pool-search-count">{T(`关键词「${poolQ}」命中 ${poolHits.length} 位候选人（含简历全文匹配）`, `"${poolQ}" matched ${poolHits.length} candidate(s) (full-resume search)`)}</p>}
          <ResponsiveTable className="w-full text-sm">
            <thead><tr className="text-left text-[11px] text-slate-400 border-b border-slate-100">
              <th className="px-3 py-2">{T("姓名", "Name")}</th><th className="px-3 py-2">{T("应聘职位", "Applied job")}</th><th className="px-3 py-2">{T("来源", "Source")}</th>
              <th className="px-3 py-2">{T("AI 评分", "AI score")}</th><th className="px-3 py-2">{poolHits ? T("匹配片段", "Match snippet") : T("联系方式", "Contact")}</th><th className="px-3 py-2"></th>
            </tr></thead>
            <tbody>
              {!ana && !poolHits ? <tr><td colSpan={6} className="text-center py-6 text-slate-300"><Loader2 className="h-4 w-4 animate-spin inline" /></td></tr> :
                (poolHits || ana.pool).length === 0 ? <tr><td colSpan={6} className="text-center py-8 text-slate-300">{poolHits ? T("无匹配候选人", "No matching candidates") : T("人才库为空", "Talent pool is empty")}</td></tr> :
                (poolHits || ana.pool).map((c) => (
                  <tr key={c.id} className="border-b border-slate-50" data-testid={`hrms-pool-row-${c.id}`}>
                    <td className="px-3 py-2 text-xs font-semibold text-slate-700">{c.name}
                      {(c.tags || []).length > 0 && (
                        <span className="mt-0.5 flex flex-wrap gap-1" data-testid={`hrms-pool-tags-${c.id}`}>
                          {c.tags.map((tg) => <span key={tg} className="text-[10px] px-1.5 py-0.5 rounded-full bg-violet-50 text-violet-600 font-semibold">{tg}</span>)}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-500">{c.jobTitle}</td>
                    <td className="px-3 py-2 text-xs text-slate-500">{SRC[c.source] || c.source}</td>
                    <td className="px-3 py-2">{scoreBadge(c.aiScore, zh)}</td>
                    <td className="px-3 py-2 text-xs text-slate-500 max-w-[280px]">{poolHits ? <span className="text-slate-600" data-testid={`hrms-pool-snippet-${c.id}`}>{snippet(c)}</span> : (c.phone || c.email || "-")}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      {canManage && <button data-testid={`hrms-pool-tag-${c.id}`} onClick={() => setTagDlg({ id: c.id, name: c.name, tags: c.tags || [], input: "" })}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full border border-violet-300 text-violet-600 hover:bg-violet-50 font-semibold px-3 py-1 mr-1.5"><Tag className="h-3 w-3" />{T("标签", "Tags")}</button>}
                      {canManage && <button data-testid={`hrms-pool-react-${c.id}`} onClick={() => reactivate(c)}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full border border-emerald-300 text-emerald-600 hover:bg-emerald-50 font-semibold px-3 py-1"><RotateCcw className="h-3 w-3" />{T("重新激活", "Reactivate")}</button>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </ResponsiveTable>
        </div>
      )}

      {/* 候选人详情 */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2 flex-wrap">
            <span>{detail?.name} · {detail?.jobTitle}</span>
            {/* 编辑候选人入口（HR 反馈 2026-10-14 · Recruiting ATS）：
                原先只能删掉重建，现预填现有资料复用「新建候选人」弹窗，保存时走 PUT 更新。 */}
            {detail && canManage && (
              <button data-testid="hrms-cand-edit-btn" onClick={() => {
                // 先取快照再关详情抽屉，避免「详情 + 编辑」两层弹窗叠在一起
                const d = detail;
                setDetail(null);
                setCandDlg({
                  id: d.id, jobId: d.jobId, name: d.name || "", source: d.source || "indeed",
                  phone: d.phone || "", email: d.email || "", expectedSalary: d.expectedSalary || "",
                  availableDate: d.availableDate || "", resumeText: d.resumeText || "",
                });
              }} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-[11px] rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-50 font-semibold px-3 py-1">
                <Pencil className="h-3 w-3" />{T("编辑资料", "Edit")}
              </button>
            )}
          </DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm" data-testid="hrms-cand-detail">
              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                <span>{T("阶段", "Stage")} <b className="text-slate-700">{stLabel(detail.stage)}</b></span>
                <span>{T("来源", "Source")} {SRC[detail.source] || detail.source}</span>
                {detail.resumeFile && (
                  <button data-testid="hrms-cand-resume-file-btn" onClick={downloadResumeFile}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-indigo-600 hover:underline inline-flex items-center gap-0.5"><FileText className="h-3 w-3" />{T("原简历", "Resume file")}</button>
                )}
                {detail.phone && <span>{detail.phone}</span>}
                {detail.email && <span>{detail.email}</span>}
                {detail.expectedSalary && <span>{T("期望", "Expects")} {sym(detail.jobId)}{detail.expectedSalary}</span>}
                {detail.availableDate && <span>{T("可到岗", "Available")} {detail.availableDate}</span>}
              </div>

              <CandidateMaterials api={api} cand={detail} zh={zh} hrUsers={hrUsers} onChanged={reload} />

              <div className="rounded-lg border border-violet-200 bg-violet-50/50 p-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                  <span className="text-xs font-bold text-violet-800">{T("AI 简历评分", "AI Resume Score")}</span>
                  {detail.aiScore != null && scoreBadge(detail.aiScore, zh)}
                  <button data-testid="hrms-cand-ai-btn" disabled={busy} onClick={() => aiScore(detail)}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto text-[11px] rounded-full border border-violet-300 text-violet-600 hover:bg-violet-100 font-semibold px-3 py-1 disabled:opacity-50">
                    {busy ? <Loader2 className="h-3 w-3 animate-spin inline" /> : detail.aiScore != null ? T("重新评分", "Rescore") : T("开始评分", "Score now")}
                  </button>
                </div>
                {detail.aiSummary && (
                  <div className="mt-2 text-xs text-slate-600 space-y-1" data-testid="hrms-cand-ai-result">
                    <p>{detail.aiSummary}</p>
                    {(detail.aiStrengths || []).length > 0 && <p className="text-emerald-700">{T("优势：", "Strengths: ")}{detail.aiStrengths.join("；")}</p>}
                    {(detail.aiRisks || []).length > 0 && <p className="text-amber-700">{T("风险：", "Risks: ")}{detail.aiRisks.join("；")}</p>}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-slate-100 p-3" data-testid="hrms-cand-interviews">
                <div className="flex items-center gap-2">
                  <CalendarClock className="h-3.5 w-3.5 text-indigo-500" />
                  <span className="text-xs font-bold text-slate-600">{T("面试安排与评价", "Interviews & Feedback")}</span>
                  <button data-testid="hrms-iv-add-btn" onClick={() => setIntForm({ date: new Date().toISOString().slice(0, 10), time: "10:00", interviewer: "", mode: "onsite" })}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 text-[11px] rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-50 font-semibold px-3 py-1"><Plus className="h-3 w-3" />{T("安排面试", "Schedule")}</button>
                </div>
                {intForm && (
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-lg bg-indigo-50/50 p-2.5" data-testid="hrms-iv-form">
                    <label className="text-[11px] text-slate-500">{T("日期", "Date")}<input data-testid="hrms-iv-date" type="date" className={inp} value={intForm.date} onChange={(e) => setIntForm((f) => ({ ...f, date: e.target.value }))} /></label>
                    <label className="text-[11px] text-slate-500">{T("时间", "Time")}<input data-testid="hrms-iv-time" type="time" className={inp} value={intForm.time} onChange={(e) => setIntForm((f) => ({ ...f, time: e.target.value }))} /></label>
                    <label className="text-[11px] text-slate-500">{T("面试官（填系统姓名可站内提醒）", "Interviewer (system name = in-app alert)")}<input data-testid="hrms-iv-interviewer" className={inp} value={intForm.interviewer} onChange={(e) => setIntForm((f) => ({ ...f, interviewer: e.target.value }))} placeholder={T("如 Tom Zhao", "e.g. Tom Zhao")} /></label>
                    <label className="text-[11px] text-slate-500">{T("方式", "Mode")}<select data-testid="hrms-iv-mode" className={inp} value={intForm.mode} onChange={(e) => setIntForm((f) => ({ ...f, mode: e.target.value }))}>{Object.entries(IM).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
                    <div className="flex-wrap md:flex-nowrap sm:col-span-2 flex justify-end gap-2">
                      <button onClick={() => setIntForm(null)} className="text-[11px] rounded-full border border-slate-200 px-3 py-1">{T("取消", "Cancel")}</button>
                      <button data-testid="hrms-iv-save-btn" disabled={busy} onClick={addInterview} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] rounded-full bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-3 py-1 disabled:opacity-50">{T("确认安排", "Confirm")}</button>
                    </div>
                  </div>
                )}
                <div className="mt-2 space-y-1.5">
                  {(detail.interviews || []).length === 0 && !intForm && <p className="text-[11px] text-slate-300">{T("暂无面试记录", "No interviews yet")}</p>}
                  {(detail.interviews || []).map((iv) => (
                    <div key={iv.id} className="flex items-center gap-2 text-[11px] text-slate-500" data-testid={`hrms-cand-iv-${iv.id}`}>
                      <span className="font-semibold text-slate-600">{T(`第${iv.round}轮`, `Round ${iv.round}`)}</span>
                      <span>{fmtD(iv.date)} {iv.time} · {IM[iv.mode]}</span>
                      {iv.interviewer && <span>· {iv.interviewer}</span>}
                      {iv.decision ? (
                        <span className={`px-1.5 py-0.5 rounded-full font-semibold ${DEC_CLS[iv.decision]}`}>{DEC[iv.decision]}{iv.score ? ` ${iv.score}/5` : ""}</span>
                      ) : <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-400">{T("待面试", "Pending")}</span>}
                      {iv.date && (
                        <button data-testid={`hrms-cand-iv-gcal-${iv.id}`} onClick={() => openCalendarLink(iv)} disabled={calBusy === iv.id}
                          title={T("打开 Google 日历模板（预填候选人，需手动保存并发送）", "Open Google Calendar template (candidate prefilled; save and send manually)")}
                          className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 ml-auto inline-flex items-center gap-1 rounded-full border border-indigo-200 text-indigo-600 hover:bg-indigo-50 font-semibold px-2 py-0.5 disabled:opacity-50">
                          {calBusy === iv.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <CalendarClock className="h-3 w-3" />}
                          {T("加入日历", "Add to Calendar")}
                        </button>
                      )}
                      {/* ml-auto 挪到「加入日历」那一段了（它现在是这一行的第一个按钮），
                          这里不要再加 ml-auto，否则两个 auto margin 会把间距切碎。 */}
                      {iv.date && <button data-testid={`hrms-cand-iv-ics-${iv.id}`} onClick={() => downloadIcs(iv)} title={T("下载日历邀请 (.ics)", "Download calendar invite (.ics)")}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-400 hover:text-indigo-600"><CalendarPlus className="h-3.5 w-3.5" /></button>}
                      <button data-testid={`hrms-cand-iv-fb-${iv.id}`} onClick={() => setFbForm({ candId: detail.id, id: iv.id, candName: detail.name, round: iv.round, score: iv.score || 3, decision: iv.decision || "pass", notes: iv.notes || "", strengths: iv.strengths || "", weaknesses: iv.weaknesses || "", conclusion: iv.conclusion || "" })}
                        className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 ${iv.date ? "" : "ml-auto "}text-indigo-600 hover:underline font-semibold`}>{iv.decision ? T("改评价", "Edit feedback") : T("记录评价", "Add feedback")}</button>
                      {canManage && <button data-testid={`hrms-cand-iv-del-${iv.id}`} onClick={async () => { await api.delete(`/hrms/candidates/${detail.id}/interviews/${iv.id}`); reload(); }} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-300 hover:text-red-500"><Trash2 className="h-3 w-3" /></button>}
                    </div>
                  ))}
                  {(detail.interviews || []).filter((iv) => iv.strengths || iv.weaknesses || iv.conclusion).map((iv) => (
                    <div key={`fb-${iv.id}`} className="rounded-md border border-slate-100 bg-white px-2 py-1.5">
                      <p className="text-[10px] font-semibold text-slate-500">{T(`第${iv.round}轮评价`, `Round ${iv.round} feedback`)}{iv.feedbackBy ? ` · ${iv.feedbackBy}` : ""}</p>
                      <FeedbackDetail iv={iv} zh={zh} />
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-testid="hrms-cand-offer">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-emerald-700">{T("Offer 信函", "Offer Letter")}</span>
                  {detail.offer ? (
                    <span className="text-[11px] text-slate-500">{CUR_SYM[detail.offer.currency] || sym(detail.jobId)}{Math.round(detail.offer.salary).toLocaleString()}/{(zh ? { annual: "年", monthly: "月", hourly: "时" } : { annual: "yr", monthly: "mo", hourly: "hr" })[detail.offer.salaryType]} · {T("入职", "Start")} {fmtD(detail.offer.hireDate)}</span>
                  ) : <span className="text-[11px] text-slate-400">{T("未设置", "Not set")}</span>}
                  <div className="ml-auto flex gap-1.5 items-center">
                    {detail.esign?.status === "signed" && <span data-testid="hrms-esign-signed" className="text-[11px] text-emerald-600 font-bold">✍️ {T("已签署", "Signed")} {fmtD(detail.esign.signedAt)}</span>}
                    {detail.esign?.status === "pending" && (
                      <button data-testid="hrms-esign-copy" onClick={async () => { const url = `${window.location.origin}/esign/offer/${detail.esign.token}`; try { await navigator.clipboard.writeText(url); toast.success(T("签署链接已复制", "Sign link copied")); } catch { toast.success(url); } }}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full bg-amber-100 text-amber-700 font-semibold px-2.5 py-1"><Copy className="h-3 w-3" />{T(`待签 ${detail.esign.requestedAt ? Math.max(0, Math.floor((Date.now() - new Date(detail.esign.requestedAt)) / 86400000)) : 0} 天 · 复制链接`, `Pending ${detail.esign.requestedAt ? Math.max(0, Math.floor((Date.now() - new Date(detail.esign.requestedAt)) / 86400000)) : 0}d · Copy link`)}</button>
                    )}
                    {canManage && (
                      <button data-testid="hrms-offer-set-btn" onClick={() => setOfferDlg({ salary: detail.offer?.salary || "", salaryType: detail.offer?.salaryType || "monthly", hireDate: detail.offer?.hireDate || new Date().toISOString().slice(0, 10), note: detail.offer?.note || "" })}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] rounded-full border border-emerald-300 text-emerald-600 hover:bg-emerald-50 font-semibold px-3 py-1">{detail.offer ? T("修改 Offer", "Edit offer") : T("设置 Offer", "Set offer")}</button>
                    )}
                    {detail.offer && canManage && detail.esign?.status !== "signed" && (
                      <button data-testid="hrms-esign-request-btn" onClick={requestEsign}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full border border-amber-400 text-amber-600 hover:bg-amber-50 font-semibold px-3 py-1"><FileSignature className="h-3 w-3" />{detail.esign?.status === "pending" ? T("重新生成链接", "Regenerate link") : T("发起电子签", "Request e-sign")}</button>
                    )}
                    {detail.offer && (
                      <button data-testid="hrms-offer-download-btn" onClick={() => downloadOffer(detail)}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1">{T("下载 Offer 信函 PDF", "Download offer PDF")}</button>
                    )}
                  </div>
                </div>
              </div>

              {detail.stage !== "hired" && (
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(STAGES).filter(([k]) => k !== "hired" && k !== detail.stage).map(([k, st]) => (
                    <button key={k} data-testid={`hrms-cand-move-${k}`} onClick={() => moveStage(detail, k)}
                      className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 font-semibold px-3 py-1">
                      <ChevronRight className="h-3 w-3" />{zh ? st.label : st.en}
                    </button>
                  ))}
                  {canManage && (
                    <button data-testid="hrms-cand-hire-btn" onClick={() => setHireDlg({ cand: detail, hireDate: new Date().toISOString().slice(0, 10), branch: jobs?.find((j) => j.id === detail.jobId)?.branch || scope || "hq" })}
                      className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-3 py-1">{T("录用入职", "Hire")}</button>
                  )}
                </div>
              )}
              {detail.stage === "hired" && <p className="text-xs text-emerald-600 font-semibold">✓ {T("已录用，员工档案与入职流程已生成", "Hired — employee record & onboarding created")}</p>}
              <OnboardingSendBlock api={api} cand={detail} branch={detail.hiredBranch || jobs?.find((j) => j.id === detail.jobId)?.branch || scope || "hq"} canManage={canManage} zh={zh} onChanged={reload} />

              {detail.resumeText && (
                <details className="rounded-lg border border-slate-100">
                  <summary className="px-3 py-2 text-xs font-bold text-slate-600 cursor-pointer">{T("简历文本", "Resume text")}</summary>
                  <pre className="px-3 pb-3 text-[11px] text-slate-500 whitespace-pre-wrap font-sans max-h-48 overflow-y-auto">{detail.resumeText}</pre>
                </details>
              )}

              <div>
                <p className="text-xs font-bold text-slate-600 mb-1">{T("阶段历史", "Stage history")}</p>
                <div className="space-y-1">
                  {(detail.stageHistory || []).slice().reverse().map((h, i) => (
                    <p key={i} className="text-[11px] text-slate-500">{fmtDT(h.at)} · <b>{stLabel(h.stage)}</b> · {h.by}{h.note ? ` · ${h.note}` : ""}</p>
                  ))}
                </div>
              </div>
              {canManage && <button data-testid="hrms-cand-del-btn" onClick={() => delCand(detail)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-[11px] text-slate-400 hover:text-red-500 inline-flex items-center gap-1"><Trash2 className="h-3 w-3" />{T("删除候选人", "Delete candidate")}</button>}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 发布/编辑职位 */}
      <Dialog open={!!jobDlg} onOpenChange={(o) => !o && setJobDlg(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{jobDlg?.id ? T("编辑职位", "Edit Job") : T("发布职位", "Post Job")}</DialogTitle></DialogHeader>
          {jobDlg && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              <label className="sm:col-span-2">{T("职位名称 *", "Job title *")}<input data-testid="hrms-job-title" className={inp} value={jobDlg.form.title} onChange={(e) => setJobDlg((d) => ({ ...d, form: { ...d.form, title: e.target.value } }))} placeholder={T("如 项目经理 PM（洛杉矶）", "e.g. Project Manager (LA)")} /></label>
              <label>{T("岗位（绑定 JD）", "Role (JD-linked)")}<select data-testid="hrms-job-role" className={inp} value={jobDlg.form.role} onChange={(e) => setJobDlg((d) => ({ ...d, form: { ...d.form, role: e.target.value } }))}>{roles.filter((r) => !r.hidden || r.role === jobDlg.form.role).map((r) => <option key={r.role} value={r.role}>{zh ? r.zh : (r.en || r.zh)}</option>)}</select></label>
              <label>{T("法人/分公司", "Entity/Branch")}<select data-testid="hrms-job-branch" className={inp} value={jobDlg.form.branch} disabled={!!scope} onChange={(e) => setJobDlg((d) => ({ ...d, form: { ...d.form, branch: e.target.value } }))}>{brOpts.map((b) => <option key={b.id} value={b.id}>{zh ? b.zh : (b.en || b.zh)}</option>)}</select></label>
              <label>{T("名额", "Openings")}<input data-testid="hrms-job-openings" type="number" min="1" className={inp} value={jobDlg.form.openings} onChange={(e) => setJobDlg((d) => ({ ...d, form: { ...d.form, openings: e.target.value } }))} /></label>
              {jobDlg.id && <label>{T("状态", "Status")}<select data-testid="hrms-job-status" className={inp} value={jobDlg.form.status} onChange={(e) => setJobDlg((d) => ({ ...d, form: { ...d.form, status: e.target.value } }))}><option value="open">{T("招聘中", "Open")}</option><option value="closed">{T("已关闭", "Closed")}</option></select></label>}
              <label className="sm:col-span-2">{T("补充要求（与岗位 JD 一起用于 AI 评分）", "Extra requirements (used with JD for AI scoring)")}<textarea rows={3} className={inp} value={jobDlg.form.description} onChange={(e) => setJobDlg((d) => ({ ...d, form: { ...d.form, description: e.target.value } }))} placeholder={T("如 需 5 年以上加州住宅施工管理经验，中英双语", "e.g. 5+ yrs CA residential construction mgmt, bilingual")} /></label>
              <div className="flex-wrap md:flex-nowrap sm:col-span-2 flex justify-end gap-2">
                <button onClick={() => setJobDlg(null)} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-job-save-btn" disabled={busy} onClick={saveJob} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存", "Save")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 添加候选人 */}
      <Dialog open={!!candDlg} onOpenChange={(o) => !o && setCandDlg(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{candDlg?.id ? T("编辑候选人", "Edit Candidate") : T("添加候选人", "Add Candidate")}</DialogTitle></DialogHeader>
          {candDlg && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {/* 应聘职位下拉：编辑时也允许修改（HRMS 走查一-3）。
                  需求原文："Please add the ability to change the job title directly when editing a candidate.
                  Currently if we need to change a candidate's job title we will need to create a brand new job post."
                  原先这里是 `disabled={!!candDlg.id}`，改职位只能「删掉候选人重建」或「删职位重建」，
                  而删职位会连带删掉该职位下所有候选人（后端已同步加了保护）。
                  现在的口径：编辑态可改职位，保存时后端会同步重算 jobTitle / jobRole。
                  注意选项来源：编辑态要**包含当前职位自身**（哪怕它已关闭），
                  否则下拉会显示为空，用户一打开编辑框就以为职位丢了。 */}
              <label className="sm:col-span-2">{T("应聘职位 *", "Job *")}<select data-testid="hrms-cand-job" className={inp} value={candDlg.jobId} onChange={(e) => setCandDlg((d) => ({ ...d, jobId: e.target.value }))}>
                <option value="">{T("请选择", "Select…")}</option>
                {(jobs || []).filter((j) => j.status === "open" || j.id === candDlg.jobId).map((j) => (
                  <option key={j.id} value={j.id}>{j.title}{j.status !== "open" ? T("（已关闭）", " (closed)") : ""}</option>
                ))}</select>
                {candDlg.id && <span className="block mt-1 text-[10px] text-slate-400">{T("改职位后，候选人在看板上的岗位归属会一起更新", "Changing the job also updates the candidate's job on the board")}</span>}</label>
              <label>{T("姓名 *", "Name *")}<input data-testid="hrms-cand-name" className={inp} value={candDlg.name} onChange={(e) => setCandDlg((d) => ({ ...d, name: e.target.value }))} /></label>
              <label>{T("来源", "Source")}<select data-testid="hrms-cand-source" className={inp} value={candDlg.source} onChange={(e) => setCandDlg((d) => ({ ...d, source: e.target.value }))}>{Object.entries(SRC).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
              <label>{T("电话", "Phone")}<input data-testid="hrms-cand-phone" className={inp} value={candDlg.phone} onChange={(e) => setCandDlg((d) => ({ ...d, phone: e.target.value }))} /></label>
              <label>{T("邮箱", "Email")}<input data-testid="hrms-cand-email" className={inp} value={candDlg.email} onChange={(e) => setCandDlg((d) => ({ ...d, email: e.target.value }))} /></label>
              <label>{T(`期望薪资 (${sym(candDlg.jobId)})`, `Expected salary (${sym(candDlg.jobId)})`)}<input data-testid="hrms-cand-salary" className={inp} value={candDlg.expectedSalary} onChange={(e) => setCandDlg((d) => ({ ...d, expectedSalary: e.target.value }))} /></label>
              <label>{T("可到岗日期", "Available date")}<input data-testid="hrms-cand-date" type="date" className={inp} value={candDlg.availableDate} onChange={(e) => setCandDlg((d) => ({ ...d, availableDate: e.target.value }))} /></label>
              <label className="sm:col-span-2">{T("简历文本（粘贴或上传 PDF 自动解析，AI 自动评分）", "Resume text (paste or upload PDF to parse; AI auto-scores)")}
                <div className="flex-wrap md:flex-nowrap flex items-center gap-2 mt-1 mb-1.5">
                  <input ref={fileRef} type="file" accept=".pdf,.txt" className="hidden" data-testid="hrms-resume-file"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setParsing(true);
                      try {
                        const fd = new FormData();
                        fd.append("file", f);
                        const { data } = await api.post("/hrms/candidates/parse-resume", fd, { headers: { "Content-Type": "multipart/form-data" } });
                        setCandDlg((d) => ({ ...d, resumeText: data.text }));
                        toast.success(T(`已解析「${data.filename}」，提取 ${data.chars} 字`, `Parsed "${data.filename}" — ${data.chars} chars`));
                      } catch (err) { toast.error(err?.response?.data?.detail || T("解析失败", "Parse failed")); }
                      setParsing(false);
                      e.target.value = "";
                    }} />
                  <button type="button" data-testid="hrms-resume-upload-btn" disabled={parsing} onClick={() => fileRef.current?.click()}
                    className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-50 font-semibold px-3 py-1 disabled:opacity-50">
                    {parsing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}{T("上传简历 PDF 自动解析", "Upload resume PDF to parse")}
                  </button>
                  {candDlg.resumeText && <span className="text-[10px] text-slate-400">{candDlg.resumeText.length} {T("字", "chars")}</span>}
                </div>
                <textarea data-testid="hrms-cand-resume" rows={5} className={inp} value={candDlg.resumeText} onChange={(e) => setCandDlg((d) => ({ ...d, resumeText: e.target.value }))} placeholder={T("粘贴候选人简历全文…", "Paste the candidate's full resume…")} /></label>
              <div className="flex-wrap md:flex-nowrap sm:col-span-2 flex justify-end gap-2">
                <button onClick={() => setCandDlg(null)} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-cand-save-btn" disabled={busy} onClick={saveCand} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : (candDlg.id ? T("保存修改", "Save changes") : T("保存并入库", "Save candidate"))}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 录用 */}
      <Dialog open={!!hireDlg} onOpenChange={(o) => !o && setHireDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{T("录用入职", "Hire")} · {hireDlg?.cand?.name}</DialogTitle></DialogHeader>
          {hireDlg && (
            <div className="space-y-3 text-sm">
              <p className="text-[11px] text-slate-400">{T("确认后将自动：创建员工档案（试用期）→ 发起入职编排（HR/主管/IT/财务四方任务清单）", "On confirm: employee record (probation) is created → onboarding tasks for HR/manager/IT/finance are started")}</p>
              <label className="block">{T("入职日期", "Hire date")}<input data-testid="hrms-hire-date" type="date" className={inp} value={hireDlg.hireDate} onChange={(e) => setHireDlg((d) => ({ ...d, hireDate: e.target.value }))} /></label>
              <label className="block">{T("法人/分公司", "Entity/Branch")}<select data-testid="hrms-hire-branch" className={inp} value={hireDlg.branch} disabled={!!scope} onChange={(e) => setHireDlg((d) => ({ ...d, branch: e.target.value }))}>{brOpts.map((b) => <option key={b.id} value={b.id}>{zh ? b.zh : (b.en || b.zh)}</option>)}</select></label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block text-xs text-slate-500">{T(`薪资 ${CUR_SYM[hireDlg.branch === "cn" ? "CNY" : "USD"]}（写入薪酬档案+Offer）`, `Salary ${CUR_SYM[hireDlg.branch === "cn" ? "CNY" : "USD"]} (saved to comp + offer)`)}<input data-testid="hrms-hire-salary" className={inp} value={hireDlg.salary || ""} onChange={(e) => setHireDlg((d) => ({ ...d, salary: e.target.value }))} placeholder={T("如 6500", "e.g. 6500")} /></label>
                <label className="block text-xs text-slate-500">{T("计薪方式", "Pay type")}<select data-testid="hrms-hire-salarytype" className={inp} value={hireDlg.salaryType || "monthly"} onChange={(e) => setHireDlg((d) => ({ ...d, salaryType: e.target.value }))}><option value="monthly">{T("月薪", "Monthly")}</option><option value="annual">{T("年薪", "Annual")}</option><option value="hourly">{T("时薪", "Hourly")}</option></select></label>
              </div>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setHireDlg(null)} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-hire-confirm-btn" disabled={busy} onClick={hire} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("确认录用", "Confirm hire")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* AI 人才推荐 */}
      <Dialog open={!!recDlg} onOpenChange={(o) => !o && setRecDlg(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{T("AI 人才推荐", "AI Talent Match")} · {recDlg?.job?.title}</DialogTitle></DialogHeader>
          {recDlg && (
            <div className="space-y-2.5 text-sm" data-testid="hrms-airec-dlg">
              <p className="text-[11px] text-slate-400">{T(`从人才库 ${recDlg.poolSize} 位沉淀候选人中匹配（AI 生成，供参考）`, `Matched from ${recDlg.poolSize} pooled candidates (AI-generated, advisory)`)}</p>
              {recDlg.items.length === 0 ? <p className="text-xs text-slate-300 text-center py-4">{T("AI 认为人才库中暂无匹配该职位的候选人", "AI found no matching candidates in the pool")}</p> :
                recDlg.items.map((r) => (
                  <div key={r.candidateId} className="rounded-lg border border-slate-100 p-3" data-testid={`hrms-airec-item-${r.candidateId}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-slate-700">{r.name}</span>
                      <span className="text-[10px] text-slate-400">{r.jobTitle}</span>
                      <span className={`ml-auto text-[11px] px-2 py-0.5 rounded-full font-bold ${r.fit >= 75 ? "bg-emerald-100 text-emerald-700" : r.fit >= 50 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500"}`}>{T("匹配", "Fit")} {r.fit}</span>
                      <button data-testid={`hrms-airec-react-${r.candidateId}`} onClick={async () => { await reactivate({ id: r.candidateId, name: r.name }); setRecDlg(null); }}
                        className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 inline-flex items-center gap-1 text-[11px] rounded-full border border-emerald-300 text-emerald-600 hover:bg-emerald-50 font-semibold px-2.5 py-1"><RotateCcw className="h-3 w-3" />{T("激活", "Activate")}</button>
                    </div>
                    <p className="text-xs text-slate-500 mt-1.5">{r.reason}</p>
                    {(r.phone || r.email) && <p className="text-[10px] text-slate-400 mt-0.5">{r.phone} {r.email}</p>}
                  </div>
                ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* 人才库技能标签 */}
      <Dialog open={!!tagDlg} onOpenChange={(o) => !o && setTagDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{T("技能标签", "Skill Tags")} · {tagDlg?.name}</DialogTitle></DialogHeader>
          {tagDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-tag-dlg">
              <p className="text-[11px] text-slate-400">{T("标签用于人才库搜索与 AI 推荐匹配，如：PMP、焊接、电工证、双语", "Tags power pool search & AI matching, e.g. PMP, welding, electrician cert, bilingual")}</p>
              <div className="flex flex-wrap gap-1.5 min-h-[28px]">
                {tagDlg.tags.length === 0 && <span className="text-xs text-slate-300">{T("暂无标签", "No tags")}</span>}
                {tagDlg.tags.map((tg) => (
                  <span key={tg} className="inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-full bg-violet-50 text-violet-700 font-semibold" data-testid={`hrms-tag-chip-${tg}`}>
                    {tg}
                    <button data-testid={`hrms-tag-remove-${tg}`} onClick={() => setTagDlg((d) => ({ ...d, tags: d.tags.filter((x) => x !== tg) }))} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-violet-400 hover:text-red-500"><X className="h-3 w-3" /></button>
                  </span>
                ))}
              </div>
              <div className="flex-wrap md:flex-nowrap flex gap-2">
                <input data-testid="hrms-tag-input" className={`${inp} flex-1 mt-0`} value={tagDlg.input}
                  onChange={(e) => setTagDlg((d) => ({ ...d, input: e.target.value }))}
                  onKeyDown={(e) => e.key === "Enter" && addTag()} placeholder={T("输入标签后回车或点添加…", "Type a tag, Enter or Add…")} />
                <button data-testid="hrms-tag-add" onClick={addTag} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full border border-slate-300 text-slate-600 font-semibold px-4">{T("添加", "Add")}</button>
              </div>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setTagDlg(null)} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-tag-save" disabled={tagBusy} onClick={saveTags}
                  className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{tagBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存标签", "Save tags")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* 入职文件包管理 */}
      <Dialog open={!!docsDlg} onOpenChange={(o) => !o && setDocsDlg(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{T("入职文件包（电子签清单）", "Onboarding Docs (e-sign checklist)")}</DialogTitle></DialogHeader>
          {docsDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-esign-docs-dlg">
              <p className="text-[11px] text-slate-400">{T("候选人签署 Offer 时需逐项确认以下文件；上传公司 PDF 模板后候选人可在签署页在线查阅。", "Candidates confirm each item when signing the offer; uploaded PDF templates are viewable on the signing page.")}</p>
              <div className="space-y-1.5 max-h-[40vh] overflow-y-auto">
                {docsDlg.map((d) => (
                  <div key={d.id} className="flex items-center gap-2 rounded-lg border border-slate-100 px-3 py-2" data-testid={`hrms-esign-doc-${d.id}`}>
                    <span className="text-xs text-slate-700 flex-1">{d.title}</span>
                    {d.filename && <span className="text-[10px] rounded-full bg-indigo-50 text-indigo-600 px-2 py-0.5 font-semibold">PDF</span>}
                    <button data-testid={`hrms-esign-doc-del-${d.id}`} onClick={() => delDocItem(d)} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-slate-300 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
              <div className="flex-wrap md:flex-nowrap flex gap-2">
                <input data-testid="hrms-esign-doc-title" className={`${inp} flex-1 mt-0`} value={docTitle} onChange={(e) => setDocTitle(e.target.value)} placeholder={T("新增确认项名称…", "New checklist item…")} />
                <button data-testid="hrms-esign-doc-add" onClick={addDocItem} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full border border-slate-300 text-slate-600 font-semibold px-4">{T("添加", "Add")}</button>
              </div>
              <input ref={docFileRef} type="file" accept=".pdf" className="hidden" data-testid="hrms-esign-doc-file"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadDocPdf(f); e.target.value = ""; }} />
              <button data-testid="hrms-esign-doc-upload" onClick={() => docFileRef.current?.click()}
                className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 w-full inline-flex items-center justify-center gap-1.5 text-xs rounded-full border border-indigo-300 text-indigo-600 hover:bg-indigo-50 font-semibold px-3 py-2"><Upload className="h-3.5 w-3.5" />{T("上传公司 PDF 文档模板", "Upload company PDF template")}</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Offer 设置 */}
      <Dialog open={!!offerDlg} onOpenChange={(o) => !o && setOfferDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{T("设置 Offer", "Set Offer")} · {detail?.name}</DialogTitle></DialogHeader>
          {offerDlg && (
            <div className="space-y-3 text-sm" data-testid="hrms-offer-dlg">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block text-xs text-slate-500">{T(`薪资 ${sym(detail?.jobId)} *`, `Salary ${sym(detail?.jobId)} *`)}<input data-testid="hrms-offer-salary" className={inp} value={offerDlg.salary} onChange={(e) => setOfferDlg((d) => ({ ...d, salary: e.target.value }))} placeholder={T("如 6500", "e.g. 6500")} /></label>
                <label className="block text-xs text-slate-500">{T("计薪方式", "Pay type")}<select data-testid="hrms-offer-salarytype" className={inp} value={offerDlg.salaryType} onChange={(e) => setOfferDlg((d) => ({ ...d, salaryType: e.target.value }))}><option value="monthly">{T("月薪", "Monthly")}</option><option value="annual">{T("年薪", "Annual")}</option><option value="hourly">{T("时薪", "Hourly")}</option></select></label>
              </div>
              <label className="block text-xs text-slate-500">{T("入职日期", "Start date")}<input data-testid="hrms-offer-hiredate" type="date" className={inp} value={offerDlg.hireDate} onChange={(e) => setOfferDlg((d) => ({ ...d, hireDate: e.target.value }))} /></label>
              <label className="block text-xs text-slate-500">{T("备注（写入信函）", "Note (in letter)")}<input data-testid="hrms-offer-note" className={inp} value={offerDlg.note} onChange={(e) => setOfferDlg((d) => ({ ...d, note: e.target.value }))} placeholder={T("如 含绩效奖金 / 签约奖…", "e.g. incl. bonus / signing bonus…")} /></label>
              <p className="text-[10px] text-slate-400">{T("保存后候选人自动进入「Offer」阶段（如尚在早期），并可下载中英双语 Offer 信函 PDF。", "Saving moves the candidate to the Offer stage and enables bilingual offer letter PDF.")}</p>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setOfferDlg(null)} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-offer-save-btn" disabled={busy} onClick={saveOffer} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存 Offer", "Save offer")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* 面试评价 */}
      <Dialog open={!!fbForm} onOpenChange={(o) => !o && setFbForm(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{T("面试评价", "Interview Feedback")} · {fbForm?.candName}（{T(`第${fbForm?.round}轮`, `Round ${fbForm?.round}`)}）</DialogTitle></DialogHeader>
          {fbForm && (
            <div className="space-y-3 text-sm" data-testid="hrms-fb-dlg">
              <label className="block text-xs text-slate-500">{T("评分（1-5）", "Score (1-5)")}
                <div className="flex gap-2 mt-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <button key={s} data-testid={`hrms-fb-score-${s}`} onClick={() => setFbForm((f) => ({ ...f, score: s }))}
                      className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 h-9 w-9 rounded-lg border text-sm font-bold ${fbForm.score === s ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{s}</button>
                  ))}
                </div>
              </label>
              <label className="block text-xs text-slate-500">{T("结论", "Decision")}
                <div className="flex gap-2 mt-1">
                  {Object.entries(DEC).map(([k, v]) => (
                    <button key={k} data-testid={`hrms-fb-dec-${k}`} onClick={() => setFbForm((f) => ({ ...f, decision: k }))}
                      className={`min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs font-bold rounded-lg px-4 py-1.5 border ${fbForm.decision === k ? "border-slate-800 bg-slate-800 text-white" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>{v}</button>
                  ))}
                </div>
              </label>
              {fbForm.round === 1 && <p className="text-[11px] text-violet-700 bg-violet-50 rounded-md px-2 py-1" data-testid="hrms-fb-round1-hint">{T("初筛（第 1 轮）由招聘专员负责：仅「评价」必填；优点 / 缺点 / 结论 按需补充，内容也可以统一写在评价里。", "Round 1 (screening) by recruiter: only Notes is required — strengths / weaknesses / conclusion are optional.")}</p>}
              <label className="block text-xs text-slate-500">{T("评价", "Notes")}{fbForm.round === 1 && <span className="text-red-500"> *</span>}
                <textarea data-testid="hrms-fb-notes" rows={3} className={inp} value={fbForm.notes} onChange={(e) => setFbForm((f) => ({ ...f, notes: e.target.value }))} placeholder={T("专业能力 / 沟通 / 文化匹配…", "Skills / communication / culture fit…")} />
              </label>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="block text-xs text-slate-500">{T("优点", "Strengths")}
                  <textarea data-testid="hrms-fb-strengths" rows={3} className={inp} value={fbForm.strengths || ""} onChange={(e) => setFbForm((f) => ({ ...f, strengths: e.target.value }))} placeholder={T("如：施工现场经验丰富、沟通清晰、有 ADU 项目经验…", "e.g. strong field experience…")} />
                </label>
                <label className="block text-xs text-slate-500">{T("缺点", "Weaknesses")}
                  <textarea data-testid="hrms-fb-weaknesses" rows={3} className={inp} value={fbForm.weaknesses || ""} onChange={(e) => setFbForm((f) => ({ ...f, weaknesses: e.target.value }))} placeholder={T("如：英文书面沟通一般、无 Procore 经验…", "e.g. limited written English…")} />
                </label>
              </div>
              <label className="block text-xs text-slate-500">{T("结论", "Conclusion")}
                <textarea data-testid="hrms-fb-conclusion" rows={2} className={inp} value={fbForm.conclusion || ""} onChange={(e) => setFbForm((f) => ({ ...f, conclusion: e.target.value }))} placeholder={T("如：建议进入复试，重点考察造价与排期能力；期望薪资偏高需谈。", "e.g. recommend round 2, probe scheduling skills…")} />
              </label>
              <div className="flex-wrap md:flex-nowrap flex justify-end gap-2">
                <button onClick={() => setFbForm(null)} className="text-xs rounded-full border border-slate-200 px-4 py-2">{T("取消", "Cancel")}</button>
                <button data-testid="hrms-fb-save" disabled={busy} onClick={saveFeedback} className="min-h-11 min-w-11 md:min-h-0 md:min-w-0 text-xs rounded-full bg-brand-500 hover:bg-brand-600 text-white font-semibold px-4 py-2 disabled:opacity-50">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : T("保存评价", "Save feedback")}</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
